import { useState, useEffect, useRef, useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link2,
} from "lucide-react";
import TiptapLink from "@tiptap/extension-link";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Camera,
  Shield,
  Globe,
  Plus,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Eye,
  EyeOff,
  ShieldAlert,
  FileSpreadsheet,
  Mail,
  MoreHorizontal,
  X,
  Upload,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, del } from "../../lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useTeamQuery, useRemoveMemberMutation, useRevokeInviteMutation } from "../../hooks/useTeam";
import {
  useMenuPermissionsQuery,
  useUpdateMenuPermissionsMutation,
  useMyMenuPermissions,
  MENU_ITEMS,
  MenuKey,
} from "../../hooks/useMenuPermissions";
import {
  useSenderIdentitiesQuery,
  useCreateSenderIdentityMutation,
  useUpdateSenderIdentityMutation,
  useDeleteSenderIdentityMutation,
  SenderIdentity,
} from "../../hooks/useSenderIdentity";
import {
  useEmailProvidersQuery,
  useEmailProviderSendStats,
  useEmailProviderCampaigns,
  useCreateEmailProviderMutation,
  useUpdateEmailProviderMutation,
  useDeleteEmailProviderMutation,
  useTestEmailProviderMutation,
  useDisconnectEmailProviderMutation,
  useBulkImportEmailProvidersMutation,
  EmailProviderConfig,
} from "../../hooks/useEmailProviders";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useConnectGoogleMailbox, useConnectMicrosoftMailbox, OAuthPopupClosedError } from "../../hooks/useEmailOAuth";
import InviteModal from "../../components/team/InviteModal";
import ConfirmModal from "../../components/ConfirmModal";
import CancelSubscriptionModal from "../../components/billing/CancelSubscriptionModal";

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  status: string;
}

interface Webhook {
  url: string;
  events: string[];
  status: string;
}

interface ProfileData {
  id: string;
  name: string;
  email: string;
  title: string | null;
  phone: string | null;
  timezone: string;
  avatar_url: string | null;
  org_role: string | null;
  two_factor_enabled: boolean;
  organization: { id: string; name: string; plan: string } | null;
}

const ALL_TABS = ["Profile", "Account", "Billing", "Email Profiles", "Team", "Menu Permissions", "Notifications"];

// Maps each tab to its permission key. Tabs without a key are always visible.
const TAB_PERM_KEY: Record<string, MenuKey> = {
  Profile: "settings_profile",
  Account: "settings_account",
  Billing: "settings_billing",
  "Email Setup": "settings_email_setup",
  "Email Profiles": "settings_email_setup",
  Team: "settings_team",
  "Menu Permissions": "settings_menu_permissions",
  Notifications: "settings_notifications",
};

// settingsTabs kept for backwards-compat (tab param validation)
const settingsTabs = ALL_TABS;

const dnsRecords: DnsRecord[] = [
  { type: "TXT", name: "leadnova._domainkey", value: "v=DKIM1; k=rsa; p=MIGfMA0GCSq...", status: "verified" },
  { type: "TXT", name: "_dmarc", value: "v=DMARC1; p=none; rua=mailto:dmarc@leadnova.com", status: "verified" },
  { type: "CNAME", name: "em.yourdomain.com", value: "u12345.wl.sendgrid.net", status: "pending" },
];

const webhooks: Webhook[] = [
  { url: "https://hooks.zapier.com/hooks/catch/123456", events: ["reply.received", "lead.created"], status: "Active" },
  { url: "https://api.company.com/webhooks/leadnova", events: ["campaign.completed"], status: "Active" },
];

interface Subscription {
  plan: string;
  plan_status: string;
  trial_ends_at: string | null;
  subscription_ends_at: string | null;
}

interface Transaction {
  id: string;
  type?: string;
  plan_slug: string | null;
  amount: string;
  currency: string;
  status: string;
  lead_credits_granted?: number | null;
  email_credits_granted?: number | null;
  created_at: string;
  stripe_invoice_id: string | null;
}

interface CreditsBalance {
  lead_credits_remaining: number;
  lead_credits_total: number;
  email_credits_remaining: number;
  email_credits_total: number;
  is_unlimited: boolean;
}

function SignatureEditor({ value, onChange }: { value: string; onChange: (html: string) => void }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      TiptapLink.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Placeholder.configure({ placeholder: "Write your email signature here…" }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChangeRef.current(editor.getHTML()),
  });

  // Sync external value when profile changes (e.g. switching rows)
  const prevValue = useRef(value);
  useEffect(() => {
    if (!editor || value === prevValue.current) return;
    prevValue.current = value;
    const { from, to } = editor.state.selection;
    editor.commands.setContent(value);
    try {
      editor.commands.setTextSelection({ from, to });
    } catch {}
  }, [editor, value]);

  const handleLink = useCallback(() => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = window.prompt("Enter URL", "https://");
    if (url) editor.chain().focus().setLink({ href: url.trim() }).run();
  }, [editor]);

  return (
    <div className="sig-editor-wrap">
      <div className="sig-editor-toolbar">
        <button
          type="button"
          className={`rre-btn${editor?.isActive("bold") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleBold().run();
          }}
          title="Bold"
        >
          <BoldIcon size={13} />
        </button>
        <button
          type="button"
          className={`rre-btn${editor?.isActive("italic") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleItalic().run();
          }}
          title="Italic"
        >
          <ItalicIcon size={13} />
        </button>
        <button
          type="button"
          className={`rre-btn${editor?.isActive("underline") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleUnderline().run();
          }}
          title="Underline"
        >
          <UnderlineIcon size={13} />
        </button>
        <span className="rre-divider" />
        <button
          type="button"
          className={`rre-btn${editor?.isActive("bulletList") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleBulletList().run();
          }}
          title="Bullet list"
        >
          <List size={13} />
        </button>
        <button
          type="button"
          className={`rre-btn${editor?.isActive("orderedList") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            editor?.chain().focus().toggleOrderedList().run();
          }}
          title="Numbered list"
        >
          <ListOrdered size={13} />
        </button>
        <span className="rre-divider" />
        <button
          type="button"
          className={`rre-btn${editor?.isActive("link") ? " rre-btn--active" : ""}`}
          onMouseDown={(e) => {
            e.preventDefault();
            handleLink();
          }}
          title="Link"
        >
          <Link2 size={13} />
        </button>
      </div>
      <EditorContent editor={editor} className="sig-editor-content" />
    </div>
  );
}

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return ALL_TABS.includes(t ?? "") ? t! : "Profile";
  });
  const [showApiKey, setShowApiKey] = useState(false);

  const { user, refreshUser, logout } = useAuth();
  const requirePasswordChange = searchParams.get("require_password_change") === "true";

  // Show success toast when redirected back after a credit purchase payment
  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      toast.success("Payment completed! Your credits will be added to your account shortly.");
      setSearchParams(
        (prev) => {
          prev.delete("payment");
          return prev;
        },
        { replace: true },
      );
    }
  }, []);
  const isOwner = user?.orgRole === "owner";
  const hasCampaignFeatures = !!user?.organization?.plan_features?.campaign_features;
  const visibleMenuItems = hasCampaignFeatures
    ? MENU_ITEMS
    : MENU_ITEMS.filter(({ key }) => key !== "campaigns" && key !== "sequences" && key !== "replies");

  const { data: myPermsData } = useMyMenuPermissions();
  const allowedMenuKeys = new Set<MenuKey>(myPermsData?.data?.allowed_menus ?? []);

  const visibleTabs = ALL_TABS.filter((tab) => {
    if (isOwner) return true;
    const key = TAB_PERM_KEY[tab];
    if (!key) return true; // no permission key means always visible
    return allowedMenuKeys.has(key);
  });

  // Reactively update activeTab when the URL search param changes (e.g. sidebar
  // link clicked while already on the Settings page).
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && visibleTabs.includes(t)) {
      setActiveTab(t);
    } else if (!t) {
      setActiveTab("Profile");
    } else if (t && !visibleTabs.includes(t)) {
      // Redirect non-owners away from owner-only tabs
      setActiveTab("Profile");
      setSearchParams({});
    }
  }, [searchParams, visibleTabs]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "Profile") {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const { data: subscriptionData, isLoading: subLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => get<Subscription>("subscription"),
    enabled: activeTab === "Billing",
  });

  const { data: transactionsData, isLoading: txLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: () => get<{ transactions: Transaction[] }>("transactions"),
    enabled: activeTab === "Billing",
  });

  const isPlanInactive = user?.planStatus === "cancelled" || user?.planStatus === "unpaid";

  const { data: creditsData } = useQuery({
    queryKey: ["credits-balance"],
    queryFn: () => get<CreditsBalance>("credits/balance"),
    enabled: activeTab === "Billing",
  });

  const subscription = subscriptionData?.data;
  const transactions = transactionsData?.data?.transactions ?? [];
  const credits = creditsData?.data;

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => post("billing/cancel", {}),
    onSuccess: async () => {
      toast.success("Subscription cancelled. You will retain access until the billing period ends.");
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
      await queryClient.invalidateQueries({ queryKey: ["credits-balance"] });
      await refreshUser();
      setShowCancelConfirm(false);
    },
    onError: () => {
      toast.error("Failed to cancel subscription. Please try again.");
      setShowCancelConfirm(false);
    },
  });

  const queryClient = useQueryClient();

  // Notification preferences
  const { data: notifData, isLoading: notifLoading } = useQuery({
    queryKey: ["notification-preferences"],
    queryFn: () => get<{ preferences: Record<string, boolean> }>("settings/notifications"),
    enabled: activeTab === "Notifications",
  });
  const notifPrefs: Record<string, boolean> = notifData?.data?.preferences ?? {};

  const updateNotifMutation = useMutation({
    mutationFn: (preferences: Record<string, boolean>) =>
      patch<{ preferences: Record<string, boolean> }>("settings/notifications", { preferences }),
    onSuccess: (res) => {
      queryClient.setQueryData(["notification-preferences"], res);
    },
    onError: () => toast.error("Failed to save preference"),
  });

  function toggleNotif(key: string, current: boolean) {
    updateNotifMutation.mutate({ [key]: !current });
  }

  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Phone formatter — formats digits as international (+XX) or US (XXX) XXX-XXXX
  function formatPhone(raw: string): string {
    const digits = raw.replace(/\D/g, "");
    if (!digits) return "";
    // International: starts with + in original input
    if (raw.trimStart().startsWith("+")) {
      if (digits.length <= 2) return "+" + digits;
      if (digits.length <= 5) return "+" + digits.slice(0, 2) + " " + digits.slice(2);
      if (digits.length <= 10) return "+" + digits.slice(0, 2) + " " + digits.slice(2, 5) + " " + digits.slice(5);
      return "+" + digits.slice(0, 2) + " " + digits.slice(2, 5) + " " + digits.slice(5, 9) + " " + digits.slice(9, 13);
    }
    // US domestic
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return "(" + digits.slice(0, 3) + ") " + digits.slice(3);
    return "(" + digits.slice(0, 3) + ") " + digits.slice(3, 6) + "-" + digits.slice(6, 10);
  }

  function handlePhoneChange(val: string): void {
    // Allow digits, spaces, +, -, (, ) only
    const cleaned = val.replace(/[^0-9+\-() ]/g, "");
    setProfilePhone(formatPhone(cleaned));
  }

  // Profile tab state
  const [profileName, setProfileName] = useState("");
  const [profileTitle, setProfileTitle] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileDialCode, setProfileDialCode] = useState("+1");
  const [profileTimezone, setProfileTimezone] = useState("UTC");
  const [companyName, setCompanyName] = useState("");
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState("");

  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => get<ProfileData>("settings"),
    enabled: activeTab === "Profile" || activeTab === "Account",
  });

  // Populate form when data loads
  useEffect(() => {
    if (profileData?.data) {
      const p = profileData.data;
      setProfileName(p.name ?? "");
      setProfileTitle(p.title ?? "");
      setProfilePhone(p.phone ? formatPhone(p.phone) : "");
      setProfileTimezone(p.timezone ?? "UTC");
      setCompanyName(p.organization?.name ?? "");
      if (p.avatar_url) setAvatarPreview(null); // use server URL, not local preview
    }
  }, [profileData?.data]);

  const profileMutation = useMutation({
    mutationFn: (data: { name: string; title: string; phone: string; timezone: string; company_name?: string }) =>
      patch<ProfileData>("settings", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      setProfileSaved(true);
      setProfileError("");
      setTimeout(() => setProfileSaved(false), 3000);
      // Refresh the auth context so nav shows updated name
      refreshUser();
    },
    onError: (err: unknown) => {
      const e = err as Error;
      setProfileError(e?.message ?? "Failed to save profile. Please try again.");
    },
  });

  // Account tab state — Change Password
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpError, setCpError] = useState("");
  const [cpSuccess, setCpSuccess] = useState(false);
  const [showCpCurrent, setShowCpCurrent] = useState(false);
  const [showCpNew, setShowCpNew] = useState(false);
  const [showCpConfirm, setShowCpConfirm] = useState(false);

  function getPasswordStrength(pw: string) {
    if (!pw) return { label: "", color: "", score: 0 };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { label: "Weak", color: "var(--red)", score: 1 };
    if (score <= 2) return { label: "Fair", color: "var(--amber)", score: 2 };
    if (score <= 3) return { label: "Good", color: "#f59e0b", score: 3 };
    return { label: "Strong", color: "var(--green)", score: 4 };
  }

  const cpStrength = getPasswordStrength(cpNew);

  // Account tab state — 2FA
  const [twoFaOtpInput, setTwoFaOtpInput] = useState("");
  const [twoFaSetupStep, setTwoFaSetupStep] = useState<"idle" | "verify">("idle");
  const [twoFaError, setTwoFaError] = useState("");
  const [showDisable2FaModal, setShowDisable2FaModal] = useState(false);
  const [disable2FaPassword, setDisable2FaPassword] = useState("");

  // Account tab state — Delete Account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  // Account tab state — Active Sessions
  const [revokingSessionId, setRevokingSessionId] = useState<number | null>(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const changePasswordMutation = useMutation({
    mutationFn: (data: { current_password: string; password: string; password_confirmation: string }) =>
      post("settings/change-password", data),
    onSuccess: () => {
      setCpSuccess(true);
      setCpCurrent("");
      setCpNew("");
      setCpConfirm("");
      setCpError("");
      setTimeout(() => {
        logout();
        navigate("/login");
      }, 2000);
    },
    onError: (err: any) => {
      const details = (err as any)?.apiError?.details;
      if (details?.current_password) setCpError(details.current_password[0]);
      else setCpError((err as Error)?.message ?? "Failed to update password.");
    },
  });

  const enable2FaMutation = useMutation({
    mutationFn: () => post("settings/2fa/enable", {}),
    onSuccess: () => {
      setTwoFaSetupStep("verify");
      setTwoFaError("");
      setTwoFaOtpInput("");
    },
    onError: (err: any) => {
      setTwoFaError((err as Error)?.message ?? "Failed to send code.");
    },
  });

  const verify2FaMutation = useMutation({
    mutationFn: (code: string) => post("settings/2fa/verify", { otp_code: code }),
    onSuccess: () => {
      setTwoFaSetupStep("idle");
      setTwoFaOtpInput("");
      setTwoFaError("");
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (err: any) => {
      setTwoFaError((err as Error)?.message ?? "Invalid code.");
    },
  });

  const disable2FaMutation = useMutation({
    mutationFn: (password: string) => del("settings/2fa", { data: { password } }),
    onSuccess: () => {
      setShowDisable2FaModal(false);
      setDisable2FaPassword("");
      setTwoFaError("");
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (err: any) => {
      setTwoFaError((err as Error)?.message ?? "Incorrect password.");
    },
  });

  // Sessions query — only fetch when Account tab is active
  interface SessionItem {
    id: number;
    device_name: string;
    ip_address: string | null;
    last_used_at: string | null;
    created_at: string | null;
    is_current: boolean;
  }
  const { data: sessionsData, refetch: refetchSessions } = useQuery({
    queryKey: ["sessions"],
    queryFn: () => get<SessionItem[]>("settings/sessions"),
    enabled: activeTab === "Account",
  });
  const sessions: SessionItem[] = sessionsData?.data ?? [];

  const handleRevokeSession = async (id: number) => {
    setRevokingSessionId(id);
    try {
      await del(`settings/sessions/${id}`);
      refetchSessions();
    } catch {
      // session already gone or error — refresh anyway
      refetchSessions();
    } finally {
      setRevokingSessionId(null);
    }
  };

  const handleRevokeAllSessions = async () => {
    setRevokingAll(true);
    try {
      await del("settings/sessions/all");
      refetchSessions();
    } catch {
      refetchSessions();
    } finally {
      setRevokingAll(false);
    }
  };

  const formatRelativeTime = (iso: string | null): string => {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileName.trim()) {
      setProfileError("Full name is required.");
      return;
    }
    setProfileError("");
    profileMutation.mutate({
      name: profileName,
      title: profileTitle,
      phone: profilePhone,
      timezone: profileTimezone,
      ...(isOwner ? { company_name: companyName } : {}),
    });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setProfileError("Image must be under 2MB.");
      return;
    }
    // Local preview immediately
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarUploading(true);
    setProfileError("");
    try {
      const formData = new FormData();
      formData.append("avatar", file);
      const token = localStorage.getItem("leadnova_user")
        ? JSON.parse(localStorage.getItem("leadnova_user")!).token
        : null;
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string).replace(/\/$/, "");
      const res = await fetch(`${apiBase}/settings/avatar`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setProfileError(data?.error?.message ?? "Upload failed. Please try again.");
        setAvatarPreview(null);
      } else {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        refreshUser();
      }
    } catch {
      setProfileError("Upload failed. Please check your connection.");
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{ inviteId: string; email: string } | null>(null);

  // Sender Identity state
  const [showSenderModal, setShowSenderModal] = useState(false);
  const [editingSender, setEditingSender] = useState<SenderIdentity | null>(null);
  const [confirmDeleteSender, setConfirmDeleteSender] = useState<{ id: string; name: string } | null>(null);
  const [senderForm, setSenderForm] = useState({
    from_name: "",
    from_email: "",
    reply_to_email: "",
    title: "",
    is_default: false,
  });
  const [senderFormError, setSenderFormError] = useState("");

  const { data: senderData, isLoading: senderLoading } = useSenderIdentitiesQuery(activeTab === "Email Setup");
  const senderIdentities = senderData?.data ?? [];
  const createSender = useCreateSenderIdentityMutation();
  const updateSender = useUpdateSenderIdentityMutation();
  const deleteSender = useDeleteSenderIdentityMutation();

  function openCreateSender() {
    setEditingSender(null);
    setSenderForm({ from_name: "", from_email: "", reply_to_email: "", title: "", is_default: false });
    setSenderFormError("");
    setShowSenderModal(true);
  }

  function openEditSender(s: SenderIdentity) {
    setEditingSender(s);
    setSenderForm({
      from_name: s.from_name,
      from_email: s.from_email,
      reply_to_email: s.reply_to_email ?? "",
      title: s.title ?? "",
      is_default: s.is_default,
    });
    setSenderFormError("");
    setShowSenderModal(true);
  }

  function handleSenderSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSenderFormError("");
    if (!senderForm.from_name.trim()) {
      setSenderFormError("From name is required.");
      return;
    }
    if (!senderForm.from_email.trim()) {
      setSenderFormError("From email is required.");
      return;
    }
    const payload = {
      from_name: senderForm.from_name,
      from_email: senderForm.from_email,
      reply_to_email: senderForm.reply_to_email || null,
      title: senderForm.title || null,
      is_default: senderForm.is_default,
    };
    if (editingSender) {
      updateSender.mutate(
        { id: editingSender.id, data: payload },
        {
          onSuccess: () => {
            setShowSenderModal(false);
            toast.success("Sender identity updated successfully.");
          },
          onError: (err: any) => setSenderFormError(err?.message ?? "Failed to update."),
        },
      );
    } else {
      createSender.mutate(payload, {
        onSuccess: () => {
          setShowSenderModal(false);
          toast.success("Sender identity created successfully.");
        },
        onError: (err: any) => setSenderFormError(err?.message ?? "Failed to create."),
      });
    }
  }

  // Email Provider state
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<EmailProviderConfig | null>(null);
  const [confirmDeleteProvider, setConfirmDeleteProvider] = useState<{ id: string; label: string } | null>(null);
  const [providerFormError, setProviderFormError] = useState("");
  const [showProviderPassword, setShowProviderPassword] = useState(false);
  const [providerForm, setProviderForm] = useState({
    provider: "custom" as "gmail" | "microsoft365" | "custom",
    label: "",
    first_name: "",
    last_name: "",
    smtp_host: "",
    smtp_port: 587,
    encryption: "tls" as "tls" | "ssl" | "starttls" | "none",
    smtp_username: "",
    sender_email: "",
    smtp_password: "",
    imap_username: "",
    imap_password: "",
    is_default: false,
    is_active: true,
    imap_host: "",
    imap_port: 993,
    imap_encryption: "ssl" as "ssl" | "tls" | "none",
    daily_campaign_limit: 30,
    warmup_enabled: true,
    warmup_limit: 60,
    warmup_increment: 1,
  });
  const [testEmailTo, setTestEmailTo] = useState("");
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);

  // OAuth connection state
  const [showOAuthModal, setShowOAuthModal] = useState(false);
  const [pendingOAuthProvider, setPendingOAuthProvider] = useState<"gmail" | "microsoft365" | null>(null);
  const [oauthTcAccepted, setOauthTcAccepted] = useState(false);
  const [oauthError, setOauthError] = useState("");
  const [customTcAccepted, setCustomTcAccepted] = useState(false);

  const { connect: connectGoogle, isPending: connectingGoogle } = useConnectGoogleMailbox();
  const { connect: connectMicrosoft, isPending: connectingMicrosoft } = useConnectMicrosoftMailbox();
  const disconnectMutation = useDisconnectEmailProviderMutation();

  // Email Profiles state
  const [showProviderTypePicker, setShowProviderTypePicker] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [bulkImportResult, setBulkImportResult] = useState<{
    created: number;
    failed: { row: number; email: string; error: string }[];
  } | null>(null);
  const [epSearchQuery, setEpSearchQuery] = useState("");
  const [epSelectedIds, setEpSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDeleteProviders, setConfirmBulkDeleteProviders] = useState(false);
  const [epOpenMenuId, setEpOpenMenuId] = useState<string | null>(null);
  const [epSelectedProfile, setEpSelectedProfile] = useState<EmailProviderConfig | null>(null);
  const [epDetailTab, setEpDetailTab] = useState<"warmup" | "settings" | "campaigns">("warmup");
  const [epSettingsForm, setEpSettingsForm] = useState({
    first_name: "",
    last_name: "",
    signature: "",
    reply_to_address: "",
    tags: [] as string[],
    daily_campaign_limit: 30,
    min_wait_time_minutes: 1,
    campaign_slow_ramp: false,
    daily_inbox_placement_limit: 10,
    custom_tracking_domain_enabled: false,
    custom_tracking_domain: "",
    warmup_enabled: true,
    warmup_limit: 60,
    warmup_increment: 1,
  });
  const [epTagInput, setEpTagInput] = useState("");
  const bulkImport = useBulkImportEmailProvidersMutation();
  const { data: epProviderData, isLoading: epProviderLoading } = useEmailProvidersQuery(activeTab === "Email Profiles");
  const epAllProviders = epProviderData?.data ?? [];
  const epCustomProviders = epAllProviders;
  const { data: epSendStatsData } = useEmailProviderSendStats(epSelectedProfile?.id ?? null);
  const { data: epCampaignsData, isLoading: epCampaignsLoading } = useEmailProviderCampaigns(
    epDetailTab === "campaigns" ? (epSelectedProfile?.id ?? null) : null
  );

  const { data: providerData, isLoading: providerLoading } = useEmailProvidersQuery(activeTab === "Email Setup");
  const emailProviders = providerData?.data ?? [];

  const isOAuthLinkedSender =
    editingSender !== null &&
    emailProviders.some(
      (p) => (p.provider === "gmail" || p.provider === "microsoft365") && p.oauth_email === editingSender.from_email,
    );
  const createEmailProvider = useCreateEmailProviderMutation();
  const updateEmailProvider = useUpdateEmailProviderMutation();
  const deleteEmailProvider = useDeleteEmailProviderMutation();
  const testEmailProvider = useTestEmailProviderMutation();

  const PROVIDER_DEFS = [
    {
      key: "gmail" as const,
      label: "Gmail / Google Workspace",
      defaultHost: "smtp.gmail.com",
      defaultPort: 587,
      defaultEncryption: "tls" as const,
    },
    {
      key: "microsoft365" as const,
      label: "Microsoft 365",
      defaultHost: "smtp.office365.com",
      defaultPort: 587,
      defaultEncryption: "starttls" as const,
    },
    {
      key: "custom" as const,
      label: "SMTP",
      defaultHost: "",
      defaultPort: 587,
      defaultEncryption: "tls" as const,
    },
  ];

  function getImapDefaults(providerKey: "gmail" | "microsoft365" | "custom") {
    if (providerKey === "gmail")
      return { imap_host: "imap.gmail.com", imap_port: 993, imap_encryption: "ssl" as const };
    if (providerKey === "microsoft365")
      return { imap_host: "outlook.office365.com", imap_port: 993, imap_encryption: "ssl" as const };
    return { imap_host: "", imap_port: 993, imap_encryption: "ssl" as const };
  }

  function openCreateEmailProvider(def: (typeof PROVIDER_DEFS)[number]) {
    setEditingProvider(null);
    setProviderForm({
      provider: def.key,
      label: "",
      first_name: "",
      last_name: "",
      smtp_host: def.defaultHost,
      smtp_port: def.defaultPort,
      encryption: def.defaultEncryption,
      smtp_username: "",
      smtp_password: "",
      sender_email: "",
      imap_username: "",
      imap_password: "",
      is_default: false,
      is_active: true,
      daily_campaign_limit: 30,
      warmup_enabled: true,
      warmup_limit: 60,
      warmup_increment: 1,
      ...getImapDefaults(def.key),
    });
    setTestEmailTo("");
    setProviderFormError("");
    setShowProviderPassword(false);
    setCustomTcAccepted(false);
    setShowProviderModal(true);
  }

  function openEditEmailProvider(config: EmailProviderConfig) {
    setEditingProvider(config);
    setProviderForm({
      provider: config.provider,
      label: config.label ?? "",
      first_name: config.first_name ?? "",
      last_name: config.last_name ?? "",
      smtp_host: config.smtp_host,
      smtp_port: config.smtp_port,
      encryption: config.encryption,
      smtp_username: config.smtp_username,
      sender_email: config.sender_email ?? config.smtp_username,
      smtp_password: "",
      imap_username: config.smtp_username ?? "",
      imap_password: "",
      is_default: config.is_default,
      is_active: config.is_active,
      imap_host: config.imap_host ?? "",
      imap_port: config.imap_port ?? 993,
      imap_encryption: (config.imap_encryption ?? "ssl") as "ssl" | "tls" | "none",
      daily_campaign_limit: config.daily_campaign_limit ?? 30,
      warmup_enabled: config.warmup_enabled ?? true,
      warmup_limit: config.warmup_limit ?? 60,
      warmup_increment: config.warmup_increment ?? 1,
    });
    setTestEmailTo(config.smtp_username);
    setProviderFormError("");
    setShowProviderPassword(false);
    setCustomTcAccepted(true);
    setShowProviderModal(true);
  }

  function handleProviderSubmit(e: React.FormEvent) {
    e.preventDefault();
    setProviderFormError("");
    if (!providerForm.smtp_host.trim()) {
      setProviderFormError("SMTP host is required.");
      return;
    }
    if (!providerForm.smtp_username.trim()) {
      setProviderFormError("Username is required.");
      return;
    }
    if (!editingProvider && !providerForm.smtp_password.trim()) {
      setProviderFormError("Password is required.");
      return;
    }

    if (editingProvider) {
      const payload: any = {
        provider: providerForm.provider,
        label: providerForm.label || null,
        first_name: providerForm.first_name || null,
        last_name: providerForm.last_name || null,
        smtp_host: providerForm.smtp_host,
        smtp_port: providerForm.smtp_port,
        encryption: providerForm.encryption,
        smtp_username: providerForm.smtp_username,
        sender_email: providerForm.sender_email || null,
        is_default: providerForm.is_default,
        is_active: providerForm.is_active,
        imap_host: providerForm.imap_host || null,
        imap_port: providerForm.imap_port,
        imap_encryption: providerForm.imap_encryption,
        daily_campaign_limit: providerForm.daily_campaign_limit,
        warmup_enabled: providerForm.warmup_enabled,
        warmup_limit: providerForm.warmup_limit,
        warmup_increment: providerForm.warmup_increment,
      };
      if (providerForm.smtp_password.trim()) {
        payload.smtp_password = providerForm.smtp_password;
      }
      updateEmailProvider.mutate(
        { id: editingProvider.id, data: payload },
        {
          onSuccess: () => {
            toast.success("Email provider updated.");
            setShowProviderModal(false);
            queryClient.invalidateQueries({ queryKey: ["sender-identities"] });
          },
          onError: (err: any) => setProviderFormError(err?.message ?? "Failed to update provider."),
        },
      );
    } else {
      createEmailProvider.mutate(
        {
          ...providerForm,
          label: providerForm.label || null,
          first_name: providerForm.first_name || null,
          last_name: providerForm.last_name || null,
          imap_host: providerForm.imap_host || null,
          daily_campaign_limit: providerForm.daily_campaign_limit,
          warmup_enabled: providerForm.warmup_enabled,
          warmup_limit: providerForm.warmup_limit,
          warmup_increment: providerForm.warmup_increment,
          tc_accepted: true,
        } as any,
        {
          onSuccess: () => {
            toast.success("Email provider configured.");
            setShowProviderModal(false);
            queryClient.invalidateQueries({ queryKey: ["sender-identities"] });
          },
          onError: (err: any) => setProviderFormError(err?.message ?? "Failed to save provider."),
        },
      );
    }
  }

  const handleOAuthConnect = async () => {
    if (!pendingOAuthProvider || !oauthTcAccepted) return;
    setOauthError("");
    try {
      if (pendingOAuthProvider === "gmail") {
        await connectGoogle();
      } else {
        await connectMicrosoft();
      }
      setShowOAuthModal(false);
      setPendingOAuthProvider(null);
      setOauthTcAccepted(false);
      toast.success("Mailbox connected successfully.");
    } catch (err: unknown) {
      if (err instanceof OAuthPopupClosedError) return; // user closed the popup — silently reset
      setOauthError(err instanceof Error ? err.message : "Connection failed. Please try again.");
    }
  };

  const handleOAuthDisconnect = async (provider: "gmail" | "microsoft365") => {
    await disconnectMutation.mutateAsync(provider);
    setShowOAuthModal(false);
    toast.success("Mailbox disconnected.");
  };

  const { data: teamData, isLoading: teamLoading } = useTeamQuery(
    activeTab === "Team" || activeTab === "Menu Permissions",
  );
  const removeMember = useRemoveMemberMutation();
  const revokeInvite = useRevokeInviteMutation();

  const members = teamData?.data?.members ?? [];
  const invitations = teamData?.data?.invitations ?? [];

  const { data: menuPermData, isLoading: menuPermLoading } = useMenuPermissionsQuery(activeTab === "Menu Permissions");
  const updateMenuPerms = useUpdateMenuPermissionsMutation();

  // Local draft state: userId -> Set of allowed MenuKeys
  const [menuPermDraft, setMenuPermDraft] = useState<Record<string, Set<MenuKey>>>({});
  const [menuPermSaving, setMenuPermSaving] = useState<Record<string, boolean>>({});

  // Initialise draft when data loads
  useEffect(() => {
    if (!menuPermData?.data?.permissions) return;
    const draft: Record<string, Set<MenuKey>> = {};
    const serverPerms = menuPermData.data.permissions;
    members
      .filter((m) => m.role !== "owner")
      .forEach((m) => {
        const found = serverPerms.find((p) => p.user_id === m.id);
        draft[m.id] = new Set(found ? found.allowed_menus : MENU_ITEMS.map((i) => i.key));
      });
    setMenuPermDraft(draft);
  }, [menuPermData, members]);

  const toggleMenuPerm = (userId: string, key: MenuKey) => {
    setMenuPermDraft((prev) => {
      const set = new Set(prev[userId] ?? MENU_ITEMS.map((i) => i.key));
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, [userId]: set };
    });
  };

  const saveMenuPerms = async (userId: string) => {
    setMenuPermSaving((prev) => ({ ...prev, [userId]: true }));
    try {
      await updateMenuPerms.mutateAsync({
        user_id: userId,
        allowed_menus: [...(menuPermDraft[userId] ?? [])] as MenuKey[],
      });
      toast.success("Permissions saved.");
    } catch {
      toast.error("Failed to save permissions.");
    } finally {
      setMenuPermSaving((prev) => ({ ...prev, [userId]: false }));
    }
  };

  function getAvatarInitials(name: string): string {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  return (
    <div className="portal-page st-page">
      <h1 className="st-title">Settings</h1>

      <div className="st-layout">
        <div className="st-nav">
          <div className="st-nav-inner">
            {visibleTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`st-nav-btn ${activeTab === tab ? "st-nav-btn-active" : ""}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="st-content">
          {activeTab === "Profile" && (
            <div className="card">
              <h2 className="st-section-title">Profile Settings</h2>
              <div className="st-avatar-row">
                {/* Wrapper keeps overflow:visible so the camera badge peeks out below the circle */}
                <div
                  className="st-avatar-wrap"
                  onClick={() => !avatarUploading && avatarInputRef.current?.click()}
                  title="Click to upload photo"
                >
                  {/* The circle itself — overflow:hidden clips the photo to the circle */}
                  <div className="st-avatar st-avatar-inner">
                    {avatarPreview || profileData?.data?.avatar_url ? (
                      <img
                        src={avatarPreview ?? profileData?.data?.avatar_url!}
                        alt="avatar"
                        className="st-avatar-img"
                      />
                    ) : profileLoading ? (
                      "…"
                    ) : (
                      getAvatarInitials(profileData?.data?.name ?? user?.fullName ?? "U")
                    )}
                  </div>
                  {/* Camera badge — outside the overflow:hidden circle, always visible */}
                  <div className="st-avatar-cam st-avatar-cam-abs">
                    {avatarUploading ? <span className="st-avatar-uploading-span">…</span> : <Camera size={12} />}
                  </div>
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="st-file-hidden"
                  onChange={handleAvatarChange}
                />
                <div>
                  <p className="st-profile-name">{profileData?.data?.name ?? user?.fullName ?? "—"}</p>
                  <p className="st-profile-email">{profileData?.data?.email ?? user?.email ?? "—"}</p>
                </div>
              </div>

              {profileError && <div className="st-alert-error">{profileError}</div>}
              {profileSaved && <div className="st-alert-success">Profile saved successfully.</div>}

              <form onSubmit={handleProfileSave}>
                <div className="st-grid-2">
                  <div className="form-group">
                    <label className="label">Full Name</label>
                    <input
                      className="input-field"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      disabled={profileMutation.isPending}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Email</label>
                    <input
                      className="input-field st-input-readonly"
                      value={profileData?.data?.email ?? user?.email ?? ""}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Company</label>
                    <input
                      className={`input-field${!isOwner ? " st-input-readonly" : ""}`}
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      readOnly={!isOwner}
                      disabled={isOwner ? profileMutation.isPending : undefined}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Title</label>
                    <input
                      className="input-field"
                      placeholder="e.g. Head of Sales"
                      value={profileTitle}
                      onChange={(e) => setProfileTitle(e.target.value)}
                      disabled={profileMutation.isPending}
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Phone</label>
                    <div className="st-phone-wrap">
                      <select
                        className="st-phone-code"
                        value={profileDialCode}
                        onChange={(e) => setProfileDialCode(e.target.value)}
                        disabled={profileMutation.isPending}
                      >
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+61">🇦🇺 +61</option>
                        <option value="+49">🇩🇪 +49</option>
                        <option value="+33">🇫🇷 +33</option>
                        <option value="+81">🇯🇵 +81</option>
                        <option value="+65">🇸🇬 +65</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+92">🇵🇰 +92</option>
                        <option value="+880">🇧🇩 +880</option>
                        <option value="+86">🇨🇳 +86</option>
                        <option value="+55">🇧🇷 +55</option>
                        <option value="+52">🇲🇽 +52</option>
                        <option value="+7">🇷🇺 +7</option>
                      </select>
                      <input
                        className="st-phone-input"
                        placeholder="555 123 4567"
                        value={profilePhone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        disabled={profileMutation.isPending}
                      />
                    </div>
                  </div>
                  {/* Timezone field hidden */}
                </div>
                <div className="st-form-footer">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={profileMutation.isPending || profileLoading}
                  >
                    {profileMutation.isPending ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === "Account" && (
            <div className="st-stack">
              {requirePasswordChange && (
                <div className="pw-change-required-banner">
                  <ShieldAlert size={20} />
                  <div>
                    <strong>Password change required.</strong>
                    <span>
                      Your password has not been changed in over 90 days. Please update it below to regain full access.
                    </span>
                  </div>
                </div>
              )}

              {/* ── Change Password ─────────────────────────────────── */}
              <div className="card">
                <h2 className="st-section-title st-section-title-mb20">Change Password</h2>
                {cpError && <div className="st-alert-error-sm">{cpError}</div>}
                {cpSuccess && (
                  <div className="st-alert-success-sm">Password updated successfully. Please log in again.</div>
                )}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!cpCurrent || !cpNew || !cpConfirm) {
                      setCpError("All fields are required.");
                      return;
                    }
                    if (cpNew.length < 8) {
                      setCpError("New password must be at least 8 characters.");
                      return;
                    }
                    if (!/[A-Z]/.test(cpNew)) {
                      setCpError("Password must contain at least one uppercase letter.");
                      return;
                    }
                    if (!/[a-z]/.test(cpNew)) {
                      setCpError("Password must contain at least one lowercase letter.");
                      return;
                    }
                    if (!/[0-9]/.test(cpNew)) {
                      setCpError("Password must contain at least one number.");
                      return;
                    }
                    if (cpNew !== cpConfirm) {
                      setCpError("New passwords do not match.");
                      return;
                    }
                    setCpError("");
                    changePasswordMutation.mutate({
                      current_password: cpCurrent,
                      password: cpNew,
                      password_confirmation: cpConfirm,
                    });
                  }}
                >
                  <div className="st-pw-form-wrap">
                    {/* Current Password */}
                    <div className="form-group">
                      <label className="label">Current Password</label>
                      <div className="st-pw-wrap">
                        <input
                          className="input-field st-pw-input"
                          type={showCpCurrent ? "text" : "password"}
                          placeholder="Enter current password"
                          value={cpCurrent}
                          onChange={(e) => setCpCurrent(e.target.value)}
                          disabled={changePasswordMutation.isPending}
                        />
                        <button type="button" onClick={() => setShowCpCurrent((v) => !v)} className="st-pw-toggle">
                          {showCpCurrent ? (
                            <EyeOff size={16} color="var(--gray-500)" />
                          ) : (
                            <Eye size={16} color="var(--gray-500)" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* New Password + strength */}
                    <div className="form-group">
                      <label className="label">New Password</label>
                      <div className="st-pw-wrap">
                        <input
                          className="input-field st-pw-input"
                          type={showCpNew ? "text" : "password"}
                          placeholder="Enter new password"
                          value={cpNew}
                          onChange={(e) => setCpNew(e.target.value)}
                          disabled={changePasswordMutation.isPending}
                        />
                        <button type="button" onClick={() => setShowCpNew((v) => !v)} className="st-pw-toggle">
                          {showCpNew ? (
                            <EyeOff size={16} color="var(--gray-500)" />
                          ) : (
                            <Eye size={16} color="var(--gray-500)" />
                          )}
                        </button>
                      </div>
                      {/* Strength bars */}
                      {cpNew && (
                        <div className="st-pw-strength">
                          <div className="st-pw-bars">
                            {[1, 2, 3, 4].map((i) => (
                              <div
                                key={i}
                                className="st-pw-bar"
                                style={{ background: i <= cpStrength.score ? cpStrength.color : "var(--gray-200)" }}
                              />
                            ))}
                          </div>
                          <span style={{ fontSize: 12, color: cpStrength.color, fontWeight: 500 }}>
                            {cpStrength.label}
                          </span>
                        </div>
                      )}
                      {/* Requirements hints */}
                      <div className="st-pw-reqs">
                        {[
                          { ok: cpNew.length >= 8, text: "At least 8 characters" },
                          { ok: /[A-Z]/.test(cpNew), text: "One uppercase letter" },
                          { ok: /[a-z]/.test(cpNew), text: "One lowercase letter" },
                          { ok: /[0-9]/.test(cpNew), text: "One number" },
                        ].map(({ ok, text }) => (
                          <div key={text} className="st-pw-req">
                            <div
                              className="st-pw-dot"
                              style={{
                                background: cpNew ? (ok ? "var(--green)" : "var(--gray-300)") : "var(--gray-300)",
                              }}
                            />
                            <span
                              style={{ color: cpNew ? (ok ? "var(--green)" : "var(--gray-400)") : "var(--gray-400)" }}
                            >
                              {text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Confirm New Password */}
                    <div className="form-group">
                      <label className="label">Confirm New Password</label>
                      <div className="st-pw-wrap">
                        <input
                          className="input-field st-pw-input"
                          type={showCpConfirm ? "text" : "password"}
                          placeholder="Confirm new password"
                          value={cpConfirm}
                          onChange={(e) => setCpConfirm(e.target.value)}
                          disabled={changePasswordMutation.isPending}
                        />
                        <button type="button" onClick={() => setShowCpConfirm((v) => !v)} className="st-pw-toggle">
                          {showCpConfirm ? (
                            <EyeOff size={16} color="var(--gray-500)" />
                          ) : (
                            <Eye size={16} color="var(--gray-500)" />
                          )}
                        </button>
                      </div>
                      {/* Match indicator */}
                      {cpConfirm && (
                        <div className="st-pw-match">
                          <div
                            className="st-pw-dot"
                            style={{ background: cpNew === cpConfirm ? "var(--green)" : "var(--red)" }}
                          />
                          <span style={{ color: cpNew === cpConfirm ? "var(--green)" : "var(--red)" }}>
                            {cpNew === cpConfirm ? "Passwords match" : "Passwords do not match"}
                          </span>
                        </div>
                      )}
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={changePasswordMutation.isPending}>
                      {changePasswordMutation.isPending ? "Updating…" : "Update Password"}
                    </button>
                  </div>
                </form>
              </div>

              {/* ── Two-Factor Authentication ────────────────────────── */}
              <div className="card">
                <h2 className="st-section-title st-section-title-mb20">Two-Factor Authentication</h2>

                {twoFaError && <div className="st-alert-error-sm">{twoFaError}</div>}

                {twoFaSetupStep === "idle" && (
                  <div className="st-2fa-row">
                    <div className="st-2fa-idle-row">
                      <Shield
                        size={20}
                        color={profileData?.data?.two_factor_enabled ? "var(--green)" : "var(--gray-500)"}
                      />
                      <div>
                        <p className="st-2fa-status-title">
                          {profileData?.data?.two_factor_enabled ? "2FA is enabled" : "2FA is currently disabled"}
                        </p>
                        <p className="st-2fa-status-sub">
                          {profileData?.data?.two_factor_enabled
                            ? "A code will be sent to your email each time you log in."
                            : "Add an extra layer of security to your account."}
                        </p>
                      </div>
                    </div>
                    {profileData?.data?.two_factor_enabled ? (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowDisable2FaModal(true);
                          setTwoFaError("");
                        }}
                      >
                        Disable 2FA
                      </button>
                    ) : (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => enable2FaMutation.mutate()}
                        disabled={enable2FaMutation.isPending}
                      >
                        {enable2FaMutation.isPending ? "Sending…" : "Enable 2FA"}
                      </button>
                    )}
                  </div>
                )}

                {twoFaSetupStep === "verify" && (
                  <div className="st-2fa-verify-wrap">
                    <p className="st-2fa-verify-hint">
                      A 6-digit code was sent to <strong>{profileData?.data?.email ?? user?.email}</strong>. Enter it
                      below to confirm setup.
                    </p>
                    <div className="form-group">
                      <label className="label">Verification Code</label>
                      <input
                        className="input-field st-2fa-verify-input"
                        placeholder="000000"
                        maxLength={6}
                        value={twoFaOtpInput}
                        onChange={(e) => setTwoFaOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        autoFocus
                      />
                    </div>
                    <div className="st-2fa-verify-actions">
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setTwoFaSetupStep("idle");
                          setTwoFaOtpInput("");
                          setTwoFaError("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={twoFaOtpInput.length !== 6 || verify2FaMutation.isPending}
                        onClick={() => verify2FaMutation.mutate(twoFaOtpInput)}
                      >
                        {verify2FaMutation.isPending ? "Verifying…" : "Verify & Enable"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Disable 2FA modal */}
                {showDisable2FaModal && (
                  <div className="cm-overlay">
                    <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                      <h3 className="cm-title">Disable Two-Factor Authentication</h3>
                      <p className="cm-message">Enter your password to confirm.</p>
                      {twoFaError && <div className="st-inline-error-sm">{twoFaError}</div>}
                      <div className="form-group st-modal-form-group-mb20">
                        <input
                          className="input-field"
                          type="password"
                          placeholder="Your password"
                          value={disable2FaPassword}
                          onChange={(e) => setDisable2FaPassword(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="cm-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setShowDisable2FaModal(false);
                            setDisable2FaPassword("");
                            setTwoFaError("");
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm cm-btn-danger"
                          disabled={!disable2FaPassword || disable2FaMutation.isPending}
                          onClick={() => disable2FaMutation.mutate(disable2FaPassword)}
                        >
                          {disable2FaMutation.isPending ? "Disabling…" : "Disable 2FA"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Active Sessions ─────────────────────────────────── */}
              <div className="card">
                <h2 className="st-section-title st-section-title-mb20">Active Sessions</h2>
                <p className="st-section-desc st-sessions-desc">Devices currently logged in to your account.</p>
                {sessions.length === 0 ? (
                  <p className="st-sessions-empty">No active sessions found.</p>
                ) : (
                  <div className="st-sessions-list">
                    {sessions.map((session) => (
                      <div key={session.id} className="st-session-row">
                        <div className="st-session-info">
                          <div className="st-session-device">
                            {session.device_name}
                            {session.is_current && <span className="st-session-badge">Current device</span>}
                          </div>
                          <div className="st-session-meta">
                            {session.ip_address ? `${session.ip_address} · ` : ""}
                            Last active {formatRelativeTime(session.last_used_at)}
                          </div>
                        </div>
                        {!session.is_current && (
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled={revokingSessionId === session.id}
                            onClick={() => handleRevokeSession(session.id)}
                          >
                            {revokingSessionId === session.id ? "Logging out…" : "Force Logout"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {sessions.filter((s) => !s.is_current).length > 0 && (
                  <div className="st-sessions-footer">
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={revokingAll}
                      onClick={handleRevokeAllSessions}
                    >
                      {revokingAll ? "Logging out…" : "Logout All Other Devices"}
                    </button>
                  </div>
                )}
              </div>

              {/* ── Danger Zone ─────────────────────────────────────── */}
              <div className="card st-danger-zone">
                <h2 className="st-danger-title">Danger Zone</h2>
                <p className="st-danger-desc">
                  {user?.orgRole === "owner"
                    ? "Deleting your account will permanently remove your entire organization, all members, and all data. This cannot be undone."
                    : "Once you delete your account, you will lose access to this organization. Please be certain."}
                </p>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => {
                    setShowDeleteModal(true);
                    setDeleteError("");
                    setDeletePassword("");
                  }}
                >
                  Delete Account
                </button>
              </div>

              {/* Delete account confirmation modal */}
              {showDeleteModal && (
                <div className="cm-overlay">
                  <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="cm-title st-modal-title-red">Delete Account</h3>
                    <p className="cm-message">
                      {user?.orgRole === "owner"
                        ? "This will delete your organization, all team members, and all associated data. Enter your password to confirm."
                        : "You will lose access to this organization. Enter your password to confirm."}
                    </p>
                    {deleteError && <div className="st-inline-error-sm">{deleteError}</div>}
                    <div className="form-group st-modal-form-group-mb20">
                      <input
                        className="input-field"
                        type="password"
                        placeholder="Enter your password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="cm-actions">
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                      </button>
                      <button
                        className="btn btn-sm cm-btn-danger"
                        disabled={!deletePassword || deleting}
                        onClick={async () => {
                          setDeleting(true);
                          setDeleteError("");
                          try {
                            await del("settings/account", { data: { password: deletePassword } });
                            logout();
                          } catch (err: any) {
                            const details = (err as any)?.apiError?.details;
                            if (details?.password) setDeleteError(details.password[0]);
                            else setDeleteError((err as Error)?.message ?? "Incorrect password.");
                            setDeleting(false);
                          }
                        }}
                      >
                        {deleting ? "Deleting…" : "Yes, Delete My Account"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "Billing" && (
            <div className="st-stack">
              <div className="card st-billing-card">
                <div className="st-billing-header">
                  <div>
                    <span className="badge badge-blue st-badge-mb8">Current Plan</span>
                    {subLoading ? (
                      <p className="st-billing-plan-status">Loading…</p>
                    ) : (
                      <>
                        <h2 className="st-billing-plan-title">{subscription?.plan ?? "Trial"} Plan</h2>
                        <p className="st-billing-plan-status">
                          Status: <span className="st-billing-plan-status-val">{subscription?.plan_status ?? "—"}</span>
                        </p>
                        <p className="st-billing-plan-status">
                          {subscription?.plan_status === "trialing" && subscription.trial_ends_at
                            ? `Trial ends ${new Date(subscription.trial_ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                            : subscription?.plan_status === "cancelled" && subscription.subscription_ends_at
                              ? `Access ends ${new Date(subscription.subscription_ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                              : subscription?.subscription_ends_at
                                ? `Next billing date: ${new Date(subscription.subscription_ends_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                                : null}
                        </p>
                      </>
                    )}
                  </div>
                  <div className="st-billing-actions">
                    <Link to="/app/upgrade-plan" className="btn btn-primary btn-sm">
                      Upgrade Plan
                    </Link>
                    {subscription?.plan_status === "active" && (
                      <button className="btn btn-danger btn-sm" onClick={() => setShowCancelConfirm(true)}>
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="st-credits-title">Credit Usage</h3>
                {(() => {
                  const leadUsed = credits
                    ? Math.max(0, credits.lead_credits_total - credits.lead_credits_remaining)
                    : 0;
                  const leadPct =
                    credits && credits.lead_credits_total > 0
                      ? Math.min(100, (leadUsed / credits.lead_credits_total) * 100)
                      : 0;
                  const leadOverLimit = credits && !credits.is_unlimited && credits.lead_credits_remaining < 0;
                  const emailUsed = credits
                    ? Math.max(0, credits.email_credits_total - credits.email_credits_remaining)
                    : 0;
                  const emailPct =
                    credits && credits.email_credits_total > 0
                      ? Math.min(100, (emailUsed / credits.email_credits_total) * 100)
                      : 0;
                  const emailOverLimit = credits && !credits.is_unlimited && credits.email_credits_remaining < 0;
                  return (
                    <div className="st-grid-2">
                      <div>
                        <div className="st-credit-row">
                          <span className="st-credit-label">Lead Credits</span>
                          <span className="st-credit-meta">
                            {credits
                              ? credits.is_unlimited
                                ? "∞ Unlimited"
                                : `${leadUsed.toLocaleString()} / ${credits.lead_credits_total.toLocaleString()}`
                              : "—"}
                            {leadOverLimit && <span className="st-over-limit-badge">Over limit</span>}
                          </span>
                        </div>
                        <div className="st-credit-bar-wrap">
                          <div
                            className="st-credit-bar"
                            style={{
                              background: leadOverLimit ? "#E53935" : "var(--sky)",
                              width: credits ? (credits.is_unlimited ? "100%" : `${leadPct}%`) : "0%",
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <div className="st-credit-row">
                          <span className="st-credit-label">Email Credits</span>
                          <span className="st-credit-meta">
                            {credits
                              ? credits.is_unlimited
                                ? "∞ Unlimited"
                                : `${emailUsed.toLocaleString()} / ${credits.email_credits_total.toLocaleString()}`
                              : "—"}
                            {emailOverLimit && <span className="st-over-limit-badge">Over limit</span>}
                          </span>
                        </div>
                        <div className="st-credit-bar-wrap">
                          <div
                            className="st-credit-bar"
                            style={{
                              background: emailOverLimit ? "#E53935" : "var(--amber)",
                              width: credits ? (credits.is_unlimited ? "100%" : `${emailPct}%`) : "0%",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {showCancelConfirm && (
                <CancelSubscriptionModal
                  subscriptionEndsAt={subscription?.subscription_ends_at ?? null}
                  isPending={cancelSubscriptionMutation.isPending}
                  onConfirm={() => cancelSubscriptionMutation.mutate()}
                  onCancel={() => setShowCancelConfirm(false)}
                />
              )}

              <div className="card">
                <h3 className="st-billing-history-title">Billing History</h3>
                <div>
                  <table className="st-table">
                    <thead>
                      <tr className="st-thead-row">
                        <th className="st-th">Date</th>
                        <th className="st-th">Description</th>
                        <th className="st-th">Credits</th>
                        <th className="st-th-right">Amount</th>
                        <th className="st-th-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txLoading && (
                        <tr>
                          <td colSpan={5} className="st-td-empty">
                            Loading…
                          </td>
                        </tr>
                      )}
                      {!txLoading && transactions.length === 0 && (
                        <tr>
                          <td colSpan={5} className="st-td-empty">
                            No transactions yet.
                          </td>
                        </tr>
                      )}
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="st-tbody-row">
                          <td className="st-td">
                            {tx.created_at
                              ? new Date(tx.created_at).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "—"}
                          </td>
                          <td className="st-td-plan">
                            {tx.plan_slug === "credit_purchase" ? "Credit Purchase" : `${tx.plan_slug ?? "—"} Plan`}
                          </td>
                          <td className="st-td">
                            {tx.plan_slug === "credit_purchase"
                              ? [
                                  tx.lead_credits_granted ? `${tx.lead_credits_granted}L` : "",
                                  tx.email_credits_granted ? `${tx.email_credits_granted}E` : "",
                                ]
                                  .filter(Boolean)
                                  .join(" + ") || "—"
                              : "—"}
                          </td>
                          <td className="st-td-amount">
                            ${parseFloat(tx.amount).toFixed(2)} {tx.currency}
                          </td>
                          <td className="st-td-status">
                            <span
                              className={`badge st-badge-capitalize ${tx.status === "succeeded" ? "badge-green" : tx.status === "failed" ? "badge-red" : tx.status === "pending" ? "badge-amber" : "badge-gray"}`}
                            >
                              {tx.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === "Email Setup" && (
            <div className="st-stack">
              <div className="card">
                <div className="st-email-setup-header">
                  <h2 className="st-section-title st-email-setup-title-inline">Sender Identity</h2>
                  {/* Add Identity button hidden */}
                </div>
                {senderLoading ? (
                  <div className="st-empty-list">Loading…</div>
                ) : senderIdentities.length === 0 ? (
                  <div className="st-empty-list">No sender identities yet. Add one to get started.</div>
                ) : (
                  <div>
                    {senderIdentities.map((s, idx) => (
                      <div key={s.id} className={`st-sender-row${idx === 0 ? " st-sender-row-first" : ""}`}>
                        <div className="st-sender-info">
                          <div className="st-sender-avatar">
                            {s.from_name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </div>
                          <div>
                            <div className="st-sender-name-row">
                              <p className="st-sender-name">{s.from_name}</p>
                              {s.is_default && <span className="badge badge-green st-sender-badge-sm">Default</span>}
                            </div>
                            {s.title && <p className="st-sender-title">{s.title}</p>}
                          </div>
                        </div>
                        <div className="st-sender-actions">
                          <button className="btn btn-ghost btn-sm st-btn-sky" onClick={() => openEditSender(s)}>
                            Edit
                          </button>
                          {!s.is_default && (
                            <button
                              className="btn btn-ghost btn-sm st-btn-red"
                              onClick={() => setConfirmDeleteSender({ id: s.id, name: s.from_name })}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sender Identity Modal */}
              {showSenderModal && (
                <div className="im-overlay">
                  <div className="im-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="st-modal-title">{editingSender ? "Edit Sender Identity" : "Add Sender Identity"}</h3>
                    <form onSubmit={handleSenderSubmit}>
                      <div className="form-group">
                        <label className="label">From Name</label>
                        <input
                          className="input-field"
                          value={senderForm.from_name}
                          onChange={(e) => setSenderForm((f) => ({ ...f, from_name: e.target.value }))}
                          placeholder="e.g. Alex Morgan"
                        />
                      </div>
                      {!(editingSender && isOAuthLinkedSender) && (
                        <div className="form-group">
                          <label className="label">Title</label>
                          <input
                            className="input-field"
                            value={senderForm.title}
                            onChange={(e) => setSenderForm((f) => ({ ...f, title: e.target.value }))}
                            placeholder="e.g. Sales Development Rep"
                          />
                        </div>
                      )}
                      <div className="form-group">
                        <label className="label">From Email</label>
                        <input
                          className="input-field"
                          type="email"
                          value={senderForm.from_email}
                          onChange={(e) => setSenderForm((f) => ({ ...f, from_email: e.target.value }))}
                          placeholder="e.g. alex@yourdomain.com"
                          readOnly={editingSender !== null && isOAuthLinkedSender}
                          style={
                            editingSender && isOAuthLinkedSender ? { opacity: 0.6, cursor: "not-allowed" } : undefined
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label className="label">Reply-To Email</label>
                        <input
                          className="input-field"
                          type="email"
                          value={senderForm.reply_to_email}
                          onChange={(e) => setSenderForm((f) => ({ ...f, reply_to_email: e.target.value }))}
                          placeholder="Optional — defaults to From Email"
                          readOnly={editingSender !== null && isOAuthLinkedSender}
                          style={
                            editingSender && isOAuthLinkedSender ? { opacity: 0.6, cursor: "not-allowed" } : undefined
                          }
                        />
                      </div>
                      <div className="st-sender-checkbox-row">
                        <input
                          type="checkbox"
                          id="sender-is-default"
                          checked={senderForm.is_default}
                          onChange={(e) => setSenderForm((f) => ({ ...f, is_default: e.target.checked }))}
                          className="st-sender-checkbox"
                        />
                        <label htmlFor="sender-is-default" className="st-sender-checkbox-label">
                          Set as default sender
                        </label>
                      </div>
                      {senderFormError && <p className="st-form-error">{senderFormError}</p>}
                      <div className="st-modal-actions">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={createSender.isPending || updateSender.isPending}
                        >
                          {editingSender ? "Save Changes" : "Add Identity"}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => setShowSenderModal(false)}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Delete Sender Identity Confirm */}
              {confirmDeleteSender && (
                <ConfirmModal
                  title="Delete Sender Identity"
                  message={`Delete "${confirmDeleteSender.name}"? This cannot be undone.`}
                  confirmLabel="Delete"
                  danger
                  onConfirm={() => {
                    deleteSender.mutate(confirmDeleteSender.id, {
                      onSuccess: () => toast.success("Sender identity deleted."),
                      onError: (err: any) => toast.error(err?.message ?? "Failed to delete."),
                    });
                    setConfirmDeleteSender(null);
                  }}
                  onCancel={() => setConfirmDeleteSender(null)}
                />
              )}

              <div className="card">
                <h2 className="st-section-title st-section-title-mb20">Email Provider</h2>
                {providerLoading ? (
                  <div className="st-provider-loading">Loading…</div>
                ) : (
                  <div className="st-grid-3">
                    {PROVIDER_DEFS.map((def) => {
                      const existing = emailProviders.find((p) => p.provider === def.key);
                      const isOAuthProvider = def.key === "gmail" || def.key === "microsoft365";
                      const oauthConnected = isOAuthProvider && existing?.oauth_connected;

                      function handleCardClick() {
                        if (isOAuthProvider) {
                          setPendingOAuthProvider(def.key as "gmail" | "microsoft365");
                          setOauthTcAccepted(false);
                          setOauthError("");
                          setShowOAuthModal(true);
                        } else if (def.key === "custom") {
                          setShowProviderTypePicker(true);
                        } else {
                          existing ? openEditEmailProvider(existing) : openCreateEmailProvider(def);
                        }
                      }

                      return (
                        <div
                          key={def.key}
                          className="st-provider-card"
                          style={{
                            borderColor: oauthConnected
                              ? "var(--green)"
                              : isOAuthProvider && existing && !oauthConnected
                                ? "var(--warning, #f59e0b)"
                                : existing?.is_default
                                  ? "var(--sky)"
                                  : existing
                                    ? "var(--green)"
                                    : "var(--gray-200)",
                          }}
                          onClick={handleCardClick}
                        >
                          <Globe
                            size={24}
                            color={
                              oauthConnected
                                ? "var(--green)"
                                : isOAuthProvider && existing && !oauthConnected
                                  ? "var(--warning, #f59e0b)"
                                  : existing?.is_default
                                    ? "var(--sky)"
                                    : existing
                                      ? "var(--green)"
                                      : "var(--gray-500)"
                            }
                            className="st-provider-card-icon"
                          />
                          <p className="st-provider-card-label">{def.label}</p>
                          {oauthConnected && existing?.oauth_email && (
                            <p className="st-provider-card-oauth-email">{existing.oauth_email}</p>
                          )}
                          <div className="st-provider-card-badges">
                            {oauthConnected && (
                              <span className="badge badge-green st-provider-badge-sm">Connected</span>
                            )}
                            {!oauthConnected && existing && isOAuthProvider && (
                              <span className="badge badge-warning st-provider-badge-sm">Authorization Required</span>
                            )}
                            {!oauthConnected && existing?.is_default && !isOAuthProvider && (
                              <span className="badge badge-green st-provider-badge-sm">Default</span>
                            )}
                            {!oauthConnected && existing && !existing.is_default && !isOAuthProvider && (
                              <span className="badge badge-gray st-provider-badge-sm">Configured</span>
                            )}
                            {!oauthConnected && !existing && isOAuthProvider && (
                              <span className="st-provider-connect-hint">Click to connect</span>
                            )}
                            {!existing && !isOAuthProvider && (
                              <span className="st-provider-connect-hint">Click to configure</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Email Provider Modal */}
              {showProviderModal && (
                <div className="im-overlay">
                  <div className="im-modal im-modal-480" onClick={(e) => e.stopPropagation()}>
                    <h3 className="st-modal-title">
                      {editingProvider
                        ? `Edit ${PROVIDER_DEFS.find((d) => d.key === editingProvider.provider)?.label ?? "Provider"}`
                        : `Configure ${PROVIDER_DEFS.find((d) => d.key === providerForm.provider)?.label ?? "Provider"}`}
                    </h3>
                    <form onSubmit={handleProviderSubmit}>
                      <div className="st-smtp-row">
                        <div className="form-group">
                          <label className="label">First Name</label>
                          <input
                            className="input-field"
                            value={providerForm.first_name}
                            onChange={(e) => setProviderForm((f) => ({ ...f, first_name: e.target.value }))}
                            placeholder="Alex"
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Last Name</label>
                          <input
                            className="input-field"
                            value={providerForm.last_name}
                            onChange={(e) => setProviderForm((f) => ({ ...f, last_name: e.target.value }))}
                            placeholder="Morgan"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="label">Email</label>
                        <input
                          className="input-field"
                          type="email"
                          value={providerForm.sender_email}
                          onChange={(e) => setProviderForm((f) => ({ ...f, sender_email: e.target.value }))}
                          placeholder="you@example.com"
                        />
                      </div>
                      <div className="st-smtp-row">
                        <div className="form-group st-smtp-host-group">
                          <label className="label">SMTP Server</label>
                          <input
                            className="input-field"
                            value={providerForm.smtp_host}
                            onChange={(e) => setProviderForm((f) => ({ ...f, smtp_host: e.target.value }))}
                            placeholder="smtp.gmail.com"
                          />
                        </div>
                        <div className="form-group st-smtp-port-group">
                          <label className="label">Port</label>
                          <input
                            className="input-field"
                            type="number"
                            value={providerForm.smtp_port}
                            onChange={(e) =>
                              setProviderForm((f) => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))
                            }
                            placeholder="587"
                            min={1}
                            max={65535}
                          />
                        </div>
                      </div>
                      {/smtp\.gmail\.com|smtp\.googlemail\.com/i.test(providerForm.smtp_host) && (
                        <p className="st-smtp-oauth-hint">
                          Gmail blocks standard passwords for SMTP. Use an <strong>App Password</strong> (Google Account
                          → Security → App Passwords), or connect via the <strong>Gmail OAuth</strong> option above for
                          best reliability.
                        </p>
                      )}
                      {/smtp\.office365\.com|smtp\.outlook\.com/i.test(providerForm.smtp_host) && (
                        <p className="st-smtp-oauth-hint">
                          Microsoft 365 requires an <strong>App Password</strong> or OAuth. For best reliability,
                          connect via the <strong>Microsoft 365 OAuth</strong> option above.
                        </p>
                      )}
                      <div className="form-group">
                        <label className="label">Encryption</label>
                        <select
                          className="input-field"
                          value={providerForm.encryption}
                          onChange={(e) => setProviderForm((f) => ({ ...f, encryption: e.target.value as any }))}
                        >
                          <option value="tls">TLS (Recommended)</option>
                          <option value="ssl">SSL</option>
                          <option value="starttls">STARTTLS</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="label">SMTP Username</label>
                        <input
                          className="input-field"
                          type="text"
                          value={providerForm.smtp_username}
                          onChange={(e) => setProviderForm((f) => ({ ...f, smtp_username: e.target.value }))}
                          placeholder="you@example.com"
                        />
                      </div>
                      <div className="form-group">
                        <label className="label">SMTP Password</label>
                        <div className="st-pw-wrap">
                          <input
                            className="input-field st-pw-input"
                            type={showProviderPassword ? "text" : "password"}
                            value={providerForm.smtp_password}
                            onChange={(e) => setProviderForm((f) => ({ ...f, smtp_password: e.target.value }))}
                            placeholder="App password or secret key"
                          />
                          <button
                            type="button"
                            className="st-pw-toggle-provider"
                            onClick={() => setShowProviderPassword((v) => !v)}
                          >
                            {showProviderPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div className="st-sender-checkbox-row">
                        <input
                          type="checkbox"
                          id="provider-is-default"
                          checked={providerForm.is_default}
                          onChange={(e) => setProviderForm((f) => ({ ...f, is_default: e.target.checked }))}
                          className="st-sender-checkbox"
                        />
                        <label htmlFor="provider-is-default" className="st-sender-checkbox-label">
                          Set as default sending provider
                        </label>
                      </div>

                      {/* IMAP Settings */}
                      <div className="st-imap-section">
                        <p className="st-imap-heading">IMAP Settings (Incoming Mail)</p>
                        <div className="form-group">
                          <label className="label">IMAP Username</label>
                          <input
                            className="input-field"
                            value={providerForm.imap_username}
                            onChange={(e) => setProviderForm((f) => ({ ...f, imap_username: e.target.value }))}
                            placeholder="you@example.com"
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">IMAP Password</label>
                          <input
                            className="input-field"
                            type="password"
                            value={providerForm.imap_password}
                            onChange={(e) => setProviderForm((f) => ({ ...f, imap_password: e.target.value }))}
                            placeholder="IMAP password"
                          />
                        </div>
                        <div className="st-imap-server-row">
                          <div>
                            <label className="label">IMAP Host</label>
                            <input
                              className="input-field"
                              value={providerForm.imap_host}
                              onChange={(e) => setProviderForm((f) => ({ ...f, imap_host: e.target.value }))}
                              placeholder="imap.example.com"
                            />
                          </div>
                          <div className="st-imap-port-group">
                            <label className="label">IMAP Port</label>
                            <input
                              className="input-field"
                              type="number"
                              value={providerForm.imap_port}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, imap_port: parseInt(e.target.value, 10) || 993 }))
                              }
                            />
                          </div>
                        </div>
                        <div>
                          <label className="label">IMAP Encryption</label>
                          <select
                            className="input-field"
                            value={providerForm.imap_encryption}
                            onChange={(e) =>
                              setProviderForm((f) => ({
                                ...f,
                                imap_encryption: e.target.value as "ssl" | "tls" | "none",
                              }))
                            }
                          >
                            <option value="ssl">SSL</option>
                            <option value="tls">TLS</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                      </div>

                      {/* Sending Limits */}
                      <div className="st-imap-section">
                        <p className="st-imap-heading">Sending Limits</p>
                        <div className="form-group">
                          <label className="label">Daily Limit</label>
                          <input
                            className="input-field"
                            type="number"
                            min={1}
                            value={providerForm.daily_campaign_limit}
                            onChange={(e) =>
                              setProviderForm((f) => ({
                                ...f,
                                daily_campaign_limit: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0),
                              }))
                            }
                          />
                        </div>
                        <div className="st-sender-checkbox-row">
                          <input
                            type="checkbox"
                            id="provider-warmup-enabled"
                            checked={providerForm.warmup_enabled}
                            onChange={(e) => setProviderForm((f) => ({ ...f, warmup_enabled: e.target.checked }))}
                            className="st-sender-checkbox"
                          />
                          <label htmlFor="provider-warmup-enabled" className="st-sender-checkbox-label">
                            Warmup Enabled
                          </label>
                        </div>
                        <div className="st-smtp-row" style={{ marginTop: 10 }}>
                          <div className="form-group">
                            <label className="label">Warmup Limit</label>
                            <input
                              className="input-field"
                              type="number"
                              min={1}
                              value={providerForm.warmup_limit}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, warmup_limit: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0) }))
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label className="label">Warmup Increment</label>
                            <input
                              className="input-field"
                              type="number"
                              min={1}
                              value={providerForm.warmup_increment}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, warmup_increment: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0) }))
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* T&C checkbox for new providers */}
                      {!editingProvider && (
                        <label className="st-tc-label">
                          <input
                            type="checkbox"
                            checked={customTcAccepted}
                            onChange={(e) => setCustomTcAccepted(e.target.checked)}
                            className="st-tc-checkbox"
                          />
                          <span className="st-tc-text">
                            I agree to the{" "}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                              Terms of Service
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                              Privacy Policy
                            </a>
                            , and authorize use of this SMTP server.
                          </span>
                        </label>
                      )}

                      {providerFormError && <p className="st-form-error">{providerFormError}</p>}
                      <div className="st-modal-actions-wrap">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={
                            createEmailProvider.isPending ||
                            updateEmailProvider.isPending ||
                            (!editingProvider && !customTcAccepted)
                          }
                        >
                          {editingProvider ? "Save Changes" : "Save Provider"}
                        </button>
                        {editingProvider && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setTestEmailTo(providerForm.smtp_username);
                              setShowTestEmailModal(true);
                            }}
                          >
                            Send Test Email
                          </button>
                        )}
                        {editingProvider && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm st-delete-btn-row"
                            onClick={() => {
                              setConfirmDeleteProvider({
                                id: editingProvider.id,
                                label:
                                  editingProvider.label ??
                                  PROVIDER_DEFS.find((d) => d.key === editingProvider.provider)?.label ??
                                  "Provider",
                              });
                              setShowProviderModal(false);
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowProviderModal(false);
                            setCustomTcAccepted(false);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* OAuth pre-connect modal */}
              {showOAuthModal &&
                pendingOAuthProvider &&
                !emailProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_connected && (
                  <div
                    className="im-overlay"
                    onClick={() => {
                      if (connectingGoogle || connectingMicrosoft) return;
                      setShowOAuthModal(false);
                      setOauthError("");
                      setOauthTcAccepted(false);
                    }}
                  >
                    <div className="im-modal im-modal-420" onClick={(e) => e.stopPropagation()}>
                      {connectingGoogle || connectingMicrosoft ? (
                        <div className="st-oauth-loading">
                          <div className="st-oauth-spinner" />
                          <p className="st-oauth-loading-title">Connecting your account…</p>
                          <p className="st-oauth-loading-sub">
                            Verifying permissions and setting up your mailbox. This will only take a moment.
                          </p>
                        </div>
                      ) : (
                        <>
                          <h3 className="st-oauth-connect-title">
                            Connect {pendingOAuthProvider === "gmail" ? "Google" : "Microsoft"} Account
                          </h3>
                          <p className="st-oauth-connect-desc">
                            LeadNova will request permission to send emails on your behalf from this account.
                            {pendingOAuthProvider === "gmail"
                              ? " Your Google App Password is not required — we use secure OAuth."
                              : " Your password is not required — we use secure OAuth."}
                          </p>

                          <label className="st-oauth-tc-label">
                            <input
                              type="checkbox"
                              checked={oauthTcAccepted}
                              onChange={(e) => setOauthTcAccepted(e.target.checked)}
                              className="st-tc-checkbox"
                            />
                            <span className="st-tc-text">
                              I agree to the{" "}
                              <a href="/terms" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                                Terms of Service
                              </a>{" "}
                              and{" "}
                              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                                Privacy Policy
                              </a>
                              , and authorize LeadNova to send emails on my behalf from this account.
                            </span>
                          </label>

                          {oauthError && <p className="st-oauth-error">{oauthError}</p>}

                          <div className="st-oauth-actions">
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowOAuthModal(false);
                                setOauthError("");
                                setOauthTcAccepted(false);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary"
                              disabled={!oauthTcAccepted}
                              onClick={handleOAuthConnect}
                            >
                              Connect with {pendingOAuthProvider === "gmail" ? "Google" : "Microsoft"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

              {/* Connected OAuth provider detail modal */}
              {showOAuthModal &&
                pendingOAuthProvider &&
                emailProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_connected && (
                  <div className="im-overlay">
                    <div className="im-modal im-modal-400" onClick={(e) => e.stopPropagation()}>
                      <h3 className="st-oauth-connected-title">
                        {pendingOAuthProvider === "gmail" ? "Gmail" : "Microsoft 365"} Connected
                      </h3>
                      <p className="st-oauth-connected-desc">
                        Sending as:{" "}
                        <strong>{emailProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_email}</strong>
                      </p>
                      <div className="st-oauth-actions">
                        <button className="btn btn-secondary" onClick={() => setShowOAuthModal(false)}>
                          Close
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleOAuthDisconnect(pendingOAuthProvider)}
                          disabled={disconnectMutation.isPending}
                        >
                          {disconnectMutation.isPending ? "Disconnecting…" : "Disconnect"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              {/* Test Email Modal */}
              {showTestEmailModal && editingProvider && (
                <div className="im-overlay">
                  <div className="im-modal im-modal-400" onClick={(e) => e.stopPropagation()}>
                    <h3 className="st-test-email-title">Send Test Email</h3>
                    <p className="st-test-email-desc">
                      Sends a test message via{" "}
                      <strong>{PROVIDER_DEFS.find((d) => d.key === editingProvider.provider)?.label}</strong> to confirm
                      your SMTP settings are working.
                    </p>
                    <div className="form-group">
                      <label className="label">To</label>
                      <input
                        className="input-field"
                        type="email"
                        value={testEmailTo}
                        onChange={(e) => setTestEmailTo(e.target.value)}
                        placeholder="recipient@example.com"
                        autoFocus
                      />
                    </div>
                    <div className="st-modal-actions">
                      <button
                        className="btn btn-primary"
                        disabled={testEmailProvider.isPending || !testEmailTo.trim()}
                        onClick={() => {
                          testEmailProvider.mutate(
                            { id: editingProvider.id, to: testEmailTo },
                            {
                              onSuccess: () => {
                                toast.success("Test email sent successfully!");
                                setShowTestEmailModal(false);
                              },
                              onError: (err: any) => toast.error(err?.message ?? "SMTP connection failed."),
                            },
                          );
                        }}
                      >
                        {testEmailProvider.isPending ? "Sending…" : "Send"}
                      </button>
                      <button className="btn btn-secondary" onClick={() => setShowTestEmailModal(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Bulk Import Modal */}
          {showBulkImportModal && (
            <div
              className="im-overlay"
              onClick={() => {
                if (!bulkImport.isPending) setShowBulkImportModal(false);
              }}
            >
              <div className="im-modal im-modal-480" onClick={(e) => e.stopPropagation()}>
                <div className="ep-picker-header">
                  <h3 className="st-modal-title" style={{ marginBottom: 4 }}>
                    Bulk Import Email Accounts
                  </h3>
                  <button
                    className="ep-close-btn"
                    onClick={() => setShowBulkImportModal(false)}
                    disabled={bulkImport.isPending}
                  >
                    <X size={18} />
                  </button>
                </div>
                {bulkImportResult ? (
                  <div className="ep-import-result">
                    <div className="ep-import-result-icon-wrap">
                      {bulkImportResult.failed.length === 0 ? (
                        <CheckCircle2 size={40} color="var(--green)" />
                      ) : bulkImportResult.created === 0 ? (
                        <XCircle size={40} color="var(--red)" />
                      ) : (
                        <AlertTriangle size={40} color="var(--amber)" />
                      )}
                    </div>
                    <p className="ep-import-result-title">
                      {bulkImportResult.failed.length === 0
                        ? "Import Complete"
                        : bulkImportResult.created === 0
                        ? "Import Failed"
                        : "Import Finished with Errors"}
                    </p>
                    <p className="ep-import-result-sub">
                      {bulkImportResult.failed.length === 0
                        ? "All accounts were successfully imported."
                        : bulkImportResult.created === 0
                        ? "No accounts could be imported. Review the errors below."
                        : "Some accounts were imported. Review the errors below."}
                    </p>
                    <div className="ep-import-stats">
                      <div className="ep-import-stat ep-import-stat--success">
                        <span className="ep-import-stat-num">{bulkImportResult.created}</span>
                        <span className="ep-import-stat-label">Imported</span>
                      </div>
                      {(bulkImportResult as any).skipped > 0 && (
                        <div className="ep-import-stat ep-import-stat--skip">
                          <span className="ep-import-stat-num">{(bulkImportResult as any).skipped}</span>
                          <span className="ep-import-stat-label">Skipped</span>
                        </div>
                      )}
                      {bulkImportResult.failed.length > 0 && (
                        <div className="ep-import-stat ep-import-stat--fail">
                          <span className="ep-import-stat-num">{bulkImportResult.failed.length}</span>
                          <span className="ep-import-stat-label">Failed</span>
                        </div>
                      )}
                    </div>
                    {bulkImportResult.failed.length > 0 && (
                      <div className="ep-import-failures">
                        <div className="ep-import-failures-header">
                          <span>Row</span>
                          <span>Email</span>
                          <span>Reason</span>
                        </div>
                        {bulkImportResult.failed.map((f) => (
                          <div key={f.row} className="ep-import-failure-row">
                            <span className="ep-import-failure-row-num">#{f.row}</span>
                            <span className="ep-import-failure-email">{f.email || "—"}</span>
                            <span className="ep-import-failure-err">{f.error}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="st-modal-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setBulkImportResult(null);
                          setBulkImportFile(null);
                          setShowBulkImportModal(false);
                        }}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="ep-picker-sub">
                      Upload a spreadsheet (.xlsx) with your email accounts.{" "}
                      <a href="/Bulk Connect - Sample sheet.xlsx" download className="ep-dl-link">
                        Download sample file
                      </a>
                    </p>
                    <label className={`ep-dropzone${bulkImportFile ? " ep-dropzone--has-file" : ""}`}>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: "none" }}
                        onChange={(e) => setBulkImportFile(e.target.files?.[0] ?? null)}
                      />
                      {bulkImportFile ? (
                        <div className="ep-dropzone-file">
                          <FileSpreadsheet size={20} color="var(--sky)" />
                          <span>{bulkImportFile.name}</span>
                          <button
                            className="ep-dropzone-remove"
                            onClick={(e) => {
                              e.preventDefault();
                              setBulkImportFile(null);
                            }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="ep-dropzone-placeholder">
                          <Upload size={24} color="var(--gray-500)" />
                          <p>Click to browse or drag &amp; drop your file here</p>
                          <p className="ep-dropzone-hint">.xlsx, .xls, .csv supported</p>
                        </div>
                      )}
                    </label>
                    <div className="st-modal-actions">
                      <button
                        className="btn btn-primary"
                        disabled={!bulkImportFile || bulkImport.isPending}
                        onClick={() => {
                          if (!bulkImportFile) return;
                          bulkImport.mutate(bulkImportFile, {
                            onSuccess: (res: any) => setBulkImportResult(res.data),
                            onError: (err: any) => toast.error(err?.message ?? "Import failed."),
                          });
                        }}
                      >
                        {bulkImport.isPending ? "Importing…" : "Import"}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowBulkImportModal(false)}
                        disabled={bulkImport.isPending}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Email Profiles Tab */}
          {activeTab === "Email Profiles" && (
            <div className="st-stack">
              {/* Provider Cards */}
              <div className="card">
                <h2 className="st-section-title st-section-title-mb20">Connect Email Account</h2>
                {epProviderLoading ? (
                  <div className="st-provider-loading">Loading…</div>
                ) : (
                  <div className="st-grid-3">
                    {PROVIDER_DEFS.map((def) => {
                      const existing = epAllProviders.find((p) => p.provider === def.key);
                      const isOAuthProvider = def.key === "gmail" || def.key === "microsoft365";
                      const oauthConnected = isOAuthProvider && existing?.oauth_connected;

                      function handleEpCardClick() {
                        if (isOAuthProvider) {
                          setPendingOAuthProvider(def.key as "gmail" | "microsoft365");
                          setOauthTcAccepted(false);
                          setOauthError("");
                          setShowOAuthModal(true);
                        } else if (def.key === "custom") {
                          setShowProviderTypePicker(true);
                        } else {
                          existing ? openEditEmailProvider(existing) : openCreateEmailProvider(def);
                        }
                      }

                      return (
                        <div
                          key={def.key}
                          className="st-provider-card"
                          style={{
                            borderColor: oauthConnected
                              ? "var(--green)"
                              : isOAuthProvider && existing && !oauthConnected
                                ? "var(--warning, #f59e0b)"
                                : existing?.is_default
                                  ? "var(--sky)"
                                  : existing
                                    ? "var(--green)"
                                    : "var(--gray-200)",
                          }}
                          onClick={handleEpCardClick}
                        >
                          <Globe
                            size={24}
                            color={
                              oauthConnected
                                ? "var(--green)"
                                : isOAuthProvider && existing && !oauthConnected
                                  ? "var(--warning, #f59e0b)"
                                  : existing?.is_default
                                    ? "var(--sky)"
                                    : existing
                                      ? "var(--green)"
                                      : "var(--gray-500)"
                            }
                            className="st-provider-card-icon"
                          />
                          <p className="st-provider-card-label">{def.label}</p>
                          {oauthConnected && existing?.oauth_email && (
                            <p className="st-provider-card-oauth-email">{existing.oauth_email}</p>
                          )}
                          <div className="st-provider-card-badges">
                            {oauthConnected && (
                              <span className="badge badge-green st-provider-badge-sm">Connected</span>
                            )}
                            {!oauthConnected && existing && isOAuthProvider && (
                              <span className="badge badge-warning st-provider-badge-sm">Authorization Required</span>
                            )}
                            {!oauthConnected && existing?.is_default && !isOAuthProvider && (
                              <span className="badge badge-green st-provider-badge-sm">Default</span>
                            )}
                            {!oauthConnected && existing && !existing.is_default && !isOAuthProvider && (
                              <span className="badge badge-gray st-provider-badge-sm">Configured</span>
                            )}
                            {!oauthConnected && !existing && isOAuthProvider && (
                              <span className="st-provider-connect-hint">Click to connect</span>
                            )}
                            {!existing && !isOAuthProvider && (
                              <span className="st-provider-connect-hint">Click to configure</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Provider Type Picker — reused for Email Profiles */}
              {showProviderTypePicker && (
                <div className="im-overlay">
                  <div className="im-modal im-modal-480" onClick={(e) => e.stopPropagation()}>
                    <div className="ep-picker-header">
                      <h3 className="st-modal-title" style={{ marginBottom: 4 }}>
                        Add SMTP Account
                      </h3>
                      <button className="ep-close-btn" onClick={() => setShowProviderTypePicker(false)}>
                        <X size={18} />
                      </button>
                    </div>
                    <p className="ep-picker-sub">Choose how you want to add your email account.</p>
                    <div className="ep-picker-cards">
                      <button
                        className="ep-picker-card"
                        onClick={() => {
                          setShowProviderTypePicker(false);
                          setBulkImportFile(null);
                          setBulkImportResult(null);
                          setShowBulkImportModal(true);
                        }}
                      >
                        <div className="ep-picker-card-icon">
                          <FileSpreadsheet size={28} color="var(--sky)" />
                        </div>
                        <p className="ep-picker-card-label">Any Provider</p>
                        <p className="ep-picker-card-title">Bulk Import from CSV</p>
                      </button>
                      <button
                        className="ep-picker-card"
                        onClick={() => {
                          setShowProviderTypePicker(false);
                          const customDef = PROVIDER_DEFS.find((d) => d.key === "custom")!;
                          openCreateEmailProvider(customDef);
                        }}
                      >
                        <div className="ep-picker-card-icon">
                          <Mail size={28} color="var(--gray-500)" />
                        </div>
                        <p className="ep-picker-card-label">Any Provider</p>
                        <p className="ep-picker-card-title">Single Account</p>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Provider Modal — reused for Email Profiles */}
              {showProviderModal && (
                <div className="im-overlay">
                  <div className="im-modal im-modal-480" onClick={(e) => e.stopPropagation()}>
                    <h3 className="st-modal-title">{editingProvider ? "Edit SMTP" : "Add SMTP Account"}</h3>
                    <form onSubmit={handleProviderSubmit}>
                      <div className="st-smtp-row">
                        <div className="form-group">
                          <label className="label">First Name</label>
                          <input
                            className="input-field"
                            value={providerForm.first_name}
                            onChange={(e) => setProviderForm((f) => ({ ...f, first_name: e.target.value }))}
                            placeholder="Alex"
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">Last Name</label>
                          <input
                            className="input-field"
                            value={providerForm.last_name}
                            onChange={(e) => setProviderForm((f) => ({ ...f, last_name: e.target.value }))}
                            placeholder="Morgan"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="label">Email</label>
                        <input
                          className="input-field"
                          type="email"
                          value={providerForm.sender_email}
                          onChange={(e) => setProviderForm((f) => ({ ...f, sender_email: e.target.value }))}
                          placeholder="you@example.com"
                        />
                      </div>
                      <div className="st-imap-section">
                        <p className="st-imap-heading">IMAP Settings (Incoming Mail)</p>
                        <div className="form-group">
                          <label className="label">IMAP Username</label>
                          <input
                            className="input-field"
                            value={providerForm.imap_username}
                            onChange={(e) => setProviderForm((f) => ({ ...f, imap_username: e.target.value }))}
                            placeholder="you@example.com"
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">IMAP Password</label>
                          <input
                            className="input-field"
                            type="password"
                            value={providerForm.imap_password}
                            onChange={(e) => setProviderForm((f) => ({ ...f, imap_password: e.target.value }))}
                            placeholder="IMAP password"
                          />
                        </div>
                        <div className="st-imap-server-row">
                          <div>
                            <label className="label">IMAP Host</label>
                            <input
                              className="input-field"
                              value={providerForm.imap_host}
                              onChange={(e) => setProviderForm((f) => ({ ...f, imap_host: e.target.value }))}
                              placeholder="imap.example.com"
                            />
                          </div>
                          <div className="st-imap-port-group">
                            <label className="label">IMAP Port</label>
                            <input
                              className="input-field"
                              type="number"
                              value={providerForm.imap_port}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, imap_port: parseInt(e.target.value, 10) || 993 }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="st-imap-section">
                        <p className="st-imap-heading">SMTP Settings (Outgoing Mail)</p>
                        <div className="form-group">
                          <label className="label">SMTP Username</label>
                          <input
                            className="input-field"
                            type="text"
                            value={providerForm.smtp_username}
                            onChange={(e) => setProviderForm((f) => ({ ...f, smtp_username: e.target.value }))}
                            placeholder="you@example.com"
                          />
                        </div>
                        <div className="form-group">
                          <label className="label">SMTP Password</label>
                          <div className="st-pw-wrap">
                            <input
                              className="input-field st-pw-input"
                              type={showProviderPassword ? "text" : "password"}
                              value={providerForm.smtp_password}
                              onChange={(e) => setProviderForm((f) => ({ ...f, smtp_password: e.target.value }))}
                              placeholder="App password or secret key"
                            />
                            <button
                              type="button"
                              className="st-pw-toggle-provider"
                              onClick={() => setShowProviderPassword((v) => !v)}
                            >
                              {showProviderPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div className="st-smtp-row">
                          <div className="form-group st-smtp-host-group">
                            <label className="label">SMTP Host</label>
                            <input
                              className="input-field"
                              value={providerForm.smtp_host}
                              onChange={(e) => setProviderForm((f) => ({ ...f, smtp_host: e.target.value }))}
                              placeholder="smtp.example.com"
                            />
                          </div>
                          <div className="form-group st-smtp-port-group">
                            <label className="label">SMTP Port</label>
                            <input
                              className="input-field"
                              type="number"
                              value={providerForm.smtp_port}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, smtp_port: parseInt(e.target.value) || 587 }))
                              }
                              placeholder="587"
                              min={1}
                              max={65535}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="st-imap-section">
                        <p className="st-imap-heading">Sending Limits</p>
                        <div className="form-group">
                          <label className="label">Daily Limit</label>
                          <input
                            className="input-field"
                            type="number"
                            min={1}
                            value={providerForm.daily_campaign_limit}
                            onChange={(e) =>
                              setProviderForm((f) => ({
                                ...f,
                                daily_campaign_limit: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0),
                              }))
                            }
                          />
                        </div>
                        <div className="st-sender-checkbox-row">
                          <input
                            type="checkbox"
                            id="ep-warmup-enabled2"
                            checked={providerForm.warmup_enabled}
                            onChange={(e) => setProviderForm((f) => ({ ...f, warmup_enabled: e.target.checked }))}
                            className="st-sender-checkbox"
                          />
                          <label htmlFor="ep-warmup-enabled2" className="st-sender-checkbox-label">
                            Warmup Enabled
                          </label>
                        </div>
                        <div className="st-smtp-row" style={{ marginTop: 10 }}>
                          <div className="form-group">
                            <label className="label">Warmup Limit</label>
                            <input
                              className="input-field"
                              type="number"
                              min={1}
                              value={providerForm.warmup_limit}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, warmup_limit: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0) }))
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label className="label">Warmup Increment</label>
                            <input
                              className="input-field"
                              type="number"
                              min={1}
                              value={providerForm.warmup_increment}
                              onChange={(e) =>
                                setProviderForm((f) => ({ ...f, warmup_increment: e.target.value === "" ? 0 : (parseInt(e.target.value, 10) || 0) }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                      {!editingProvider && (
                        <label className="st-tc-label">
                          <input
                            type="checkbox"
                            checked={customTcAccepted}
                            onChange={(e) => setCustomTcAccepted(e.target.checked)}
                            className="st-tc-checkbox"
                          />
                          <span className="st-tc-text">
                            I agree to the{" "}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                              Terms of Service
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                              Privacy Policy
                            </a>
                            , and authorize use of this SMTP server.
                          </span>
                        </label>
                      )}
                      {providerFormError && <p className="st-form-error">{providerFormError}</p>}
                      <div className="st-modal-actions-wrap">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={
                            createEmailProvider.isPending ||
                            updateEmailProvider.isPending ||
                            (!editingProvider && !customTcAccepted)
                          }
                        >
                          {editingProvider ? "Save Changes" : "Save Account"}
                        </button>
                        {editingProvider && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm st-delete-btn-row"
                            onClick={() => {
                              setConfirmDeleteProvider({
                                id: editingProvider.id,
                                label: editingProvider.label ?? editingProvider.smtp_username,
                              });
                              setShowProviderModal(false);
                            }}
                          >
                            Delete
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowProviderModal(false);
                            setCustomTcAccepted(false);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* OAuth modals reused */}
              {showOAuthModal &&
                pendingOAuthProvider &&
                !epAllProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_connected && (
                  <div
                    className="im-overlay"
                    onClick={() => {
                      if (connectingGoogle || connectingMicrosoft) return;
                      setShowOAuthModal(false);
                      setOauthError("");
                      setOauthTcAccepted(false);
                    }}
                  >
                    <div className="im-modal im-modal-420" onClick={(e) => e.stopPropagation()}>
                      {connectingGoogle || connectingMicrosoft ? (
                        <div className="st-oauth-loading">
                          <div className="st-oauth-spinner" />
                          <p className="st-oauth-loading-title">Connecting your account…</p>
                          <p className="st-oauth-loading-sub">Verifying permissions and setting up your mailbox.</p>
                        </div>
                      ) : (
                        <>
                          <h3 className="st-oauth-connect-title">
                            Connect {pendingOAuthProvider === "gmail" ? "Google" : "Microsoft"} Account
                          </h3>
                          <p className="st-oauth-connect-desc">
                            LeadNova will request permission to send emails on your behalf.
                          </p>
                          <label className="st-oauth-tc-label">
                            <input
                              type="checkbox"
                              checked={oauthTcAccepted}
                              onChange={(e) => setOauthTcAccepted(e.target.checked)}
                              className="st-tc-checkbox"
                            />
                            <span className="st-tc-text">
                              I agree to the{" "}
                              <a href="/terms" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                                Terms of Service
                              </a>{" "}
                              and{" "}
                              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="st-tc-link">
                                Privacy Policy
                              </a>
                              , and authorize LeadNova to send emails on my behalf.
                            </span>
                          </label>
                          {oauthError && <p className="st-oauth-error">{oauthError}</p>}
                          <div className="st-oauth-actions">
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowOAuthModal(false);
                                setOauthError("");
                                setOauthTcAccepted(false);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary"
                              disabled={!oauthTcAccepted}
                              onClick={handleOAuthConnect}
                            >
                              Connect with {pendingOAuthProvider === "gmail" ? "Google" : "Microsoft"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              {showOAuthModal &&
                pendingOAuthProvider &&
                epAllProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_connected && (
                  <div className="im-overlay">
                    <div className="im-modal im-modal-400" onClick={(e) => e.stopPropagation()}>
                      <h3 className="st-oauth-connected-title">
                        {pendingOAuthProvider === "gmail" ? "Gmail" : "Microsoft 365"} Connected
                      </h3>
                      <p className="st-oauth-connected-desc">
                        Sending as:{" "}
                        <strong>{epAllProviders.find((p) => p.provider === pendingOAuthProvider)?.oauth_email}</strong>
                      </p>
                      <div className="st-oauth-actions">
                        <button className="btn btn-secondary" onClick={() => setShowOAuthModal(false)}>
                          Close
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleOAuthDisconnect(pendingOAuthProvider)}
                          disabled={disconnectMutation.isPending}
                        >
                          {disconnectMutation.isPending ? "Disconnecting…" : "Disconnect"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              <div className="card ep-profiles-card">
                <div className="ep-profiles-toolbar">
                  <input
                    type="text"
                    className="input-field ep-search-input"
                    placeholder="Search..."
                    value={epSearchQuery}
                    onChange={(e) => setEpSearchQuery(e.target.value)}
                  />
                  <div className="ep-profiles-toolbar-right">
                    {epSelectedIds.size > 0 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setConfirmBulkDeleteProviders(true)}
                      >
                        Delete ({epSelectedIds.size})
                      </button>
                    )}
                  </div>
                </div>

                {epProviderLoading ? (
                  <div className="ep-loading">Loading email profiles…</div>
                ) : epCustomProviders.length === 0 ? (
                  <div className="ep-empty">
                    <Mail size={36} color="var(--gray-200)" />
                    <p>
                      No email accounts yet. Click <strong>Add New</strong> to get started.
                    </p>
                  </div>
                ) : (
                  <div>
                    <table className="st-table">
                      <thead>
                        <tr className="st-thead-row">
                          <th className="st-team-th ep-th-check">
                            {(() => {
                              const visibleIds = epCustomProviders
                                .filter((p) => !epSearchQuery || p.smtp_username.toLowerCase().includes(epSearchQuery.toLowerCase()))
                                .map((p) => p.id);
                              const allChecked = visibleIds.length > 0 && visibleIds.every((id) => epSelectedIds.has(id));
                              const someChecked = visibleIds.some((id) => epSelectedIds.has(id));
                              return (
                                <input
                                  type="checkbox"
                                  className="st-sender-checkbox"
                                  checked={allChecked}
                                  ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                                  onChange={() => {
                                    if (allChecked) {
                                      setEpSelectedIds(new Set());
                                    } else {
                                      setEpSelectedIds(new Set(visibleIds));
                                    }
                                  }}
                                />
                              );
                            })()}
                          </th>
                          <th className="st-team-th">EMAIL</th>
                          <th className="st-team-th">SOURCE</th>
                          <th className="st-team-th">STATUS</th>
                          <th className="st-team-th">EMAILS SENT</th>
                          <th className="st-team-th">WARMUP EMAILS</th>
                          <th className="st-team-th">HEALTH SCORE</th>
                          <th className="st-team-th"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {epCustomProviders
                          .filter(
                            (p) =>
                              !epSearchQuery || p.smtp_username.toLowerCase().includes(epSearchQuery.toLowerCase()),
                          )
                          .map((provider) => (
                            <tr
                              key={provider.id}
                              className="st-tbody-row ep-profile-row"
                              onClick={() => {
                                setEpSelectedProfile(provider);
                                setEpDetailTab("warmup");
                                setEpSettingsForm({
                                  first_name: provider.first_name ?? "",
                                  last_name: provider.last_name ?? "",
                                  signature: provider.signature ?? "",
                                  reply_to_address: provider.reply_to_address ?? "",
                                  tags: provider.tags ?? [],
                                  daily_campaign_limit: provider.daily_campaign_limit ?? 30,
                                  min_wait_time_minutes: provider.min_wait_time_minutes ?? 1,
                                  campaign_slow_ramp: provider.campaign_slow_ramp ?? false,
                                  daily_inbox_placement_limit: provider.daily_inbox_placement_limit ?? 10,
                                  custom_tracking_domain_enabled: provider.custom_tracking_domain_enabled ?? false,
                                  custom_tracking_domain: provider.custom_tracking_domain ?? "",
                                  warmup_enabled: provider.warmup_enabled ?? true,
                                  warmup_limit: provider.warmup_limit ?? 60,
                                  warmup_increment: provider.warmup_increment ?? 1,
                                });
                              }}
                            >
                              <td className="st-team-td ep-td-check" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="st-sender-checkbox"
                                  checked={epSelectedIds.has(provider.id)}
                                  onChange={() => {
                                    setEpSelectedIds((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(provider.id)) next.delete(provider.id);
                                      else next.add(provider.id);
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td className="st-team-td ep-td-email">
                                <div className="ep-email-cell">
                                  <div className="ep-email-avatar">
                                    {(provider.smtp_username[0] ?? "E").toUpperCase()}
                                  </div>
                                  <span>{provider.smtp_username}</span>
                                </div>
                              </td>
                              <td className="st-team-td">
                                <span className={`ep-origin-badge ep-origin-${provider.provider}`}>
                                  {provider.provider === "gmail"
                                    ? "Google"
                                    : provider.provider === "microsoft365"
                                      ? "Microsoft"
                                      : "SMTP"}
                                </span>
                              </td>
                              <td className="st-team-td">
                                <span
                                  className={`ep-origin-badge ${provider.is_active ? "ep-status-active" : "ep-status-inactive"}`}
                                >
                                  {provider.is_active ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td className="st-team-td">{provider.emails_sent_today ?? 0} of {provider.warmup_enabled ? (provider.warmup_limit ?? 60) : (provider.daily_campaign_limit ?? 30)}</td>
                              <td className="st-team-td">{provider.warmup_enabled ? (provider.warmup_limit ?? 60) : (provider.daily_campaign_limit ?? 30)}</td>
                              <td className="st-team-td">
                                <span className={`ep-health-score${!provider.is_active ? " ep-health-score--inactive" : ""}`}>
                                  {provider.is_active ? "100%" : "0%"}
                                </span>
                              </td>
                              <td className="st-team-td ep-td-actions" onClick={(e) => e.stopPropagation()}>
                                <div className="cp-overflow-wrap">
                                  <button
                                    className="btn btn-secondary btn-sm cp-more-btn"
                                    onClick={() => setEpOpenMenuId(epOpenMenuId === provider.id ? null : provider.id)}
                                  >
                                    <MoreHorizontal size={15} />
                                  </button>
                                  {epOpenMenuId === provider.id && (
                                    <div className="cp-dropdown ep-dropdown">
                                      <button
                                        className="cp-dropdown-item"
                                        onClick={() => {
                                          setEpOpenMenuId(null);
                                          updateEmailProvider.mutate(
                                            { id: provider.id, data: { is_active: !provider.is_active } },
                                            {
                                              onSuccess: () =>
                                                toast.success(
                                                  provider.is_active ? "Account disabled." : "Account enabled.",
                                                ),
                                              onError: (err: any) => toast.error(err?.message ?? "Failed to update."),
                                            },
                                          );
                                        }}
                                      >
                                        {provider.is_active ? "Disconnect" : "Connect"}
                                      </button>
                                      <button
                                        className="cp-dropdown-item cp-dropdown-delete-item"
                                        onClick={() => {
                                          setEpOpenMenuId(null);
                                          if (provider.provider === "gmail" || provider.provider === "microsoft365") {
                                            setPendingOAuthProvider(provider.provider as "gmail" | "microsoft365");
                                            setShowOAuthModal(true);
                                          } else {
                                            setConfirmDeleteProvider({
                                              id: provider.id,
                                              label: provider.smtp_username,
                                            });
                                          }
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email Account Detail Modal */}
          {epSelectedProfile && (
            <div
              className="im-overlay"
              onClick={() => {
                setEpSelectedProfile(null);
                setEpOpenMenuId(null);
              }}
            >
              <div className="ep-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ep-detail-header">
                  <div className="ep-detail-header-left">
                    <CheckCircle2 size={16} color={epSelectedProfile.is_active ? "var(--green)" : "var(--gray-400)"} />
                    <span className="ep-detail-email">{epSelectedProfile.smtp_username}</span>
                    <span
                      className={`ep-origin-badge ${epSelectedProfile.is_active ? "ep-status-active" : "ep-status-inactive"}`}
                    >
                      {epSelectedProfile.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="ep-detail-header-right">
                    <button
                      className={`btn btn-sm ${epSelectedProfile.is_active ? "btn-secondary" : "btn-primary"}`}
                      disabled={updateEmailProvider.isPending}
                      onClick={() => {
                        const newActive = !epSelectedProfile.is_active;
                        updateEmailProvider.mutate(
                          { id: epSelectedProfile.id, data: { is_active: newActive } },
                          {
                            onSuccess: () => {
                              setEpSelectedProfile((p) => (p ? { ...p, is_active: newActive } : p));
                              toast.success(newActive ? "Account enabled." : "Account disabled.");
                            },
                            onError: (err: any) => toast.error(err?.message ?? "Failed to update."),
                          },
                        );
                      }}
                    >
                      {epSelectedProfile.is_active ? "Disable" : "Enable"}
                    </button>
                    <button className="ep-close-btn" onClick={() => setEpSelectedProfile(null)}>
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="cp-tabs ep-detail-tabs">
                  {(["warmup", "settings", "campaigns"] as const).map((tab) => (
                    <button
                      key={tab}
                      className={`cp-tab${epDetailTab === tab ? " cp-tab--active" : ""}`}
                      onClick={() => setEpDetailTab(tab)}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Warmup Tab */}
                {epDetailTab === "warmup" && (
                  <div className="ep-detail-body">
                    <div className="ep-warmup-meta">
                      <span>
                        Started on{" "}
                        <strong>
                          {epSelectedProfile.created_at
                            ? new Date(epSelectedProfile.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </strong>
                      </span>
                      <div className="ep-warmup-actions">
                        <span
                          className={`ep-origin-badge ${epSelectedProfile.warmup_enabled ? "ep-status-active" : "ep-status-inactive"}`}
                          style={{ marginRight: 8 }}
                        >
                          Warmup {epSelectedProfile.warmup_enabled ? "On" : "Off"}
                        </span>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={!epSelectedProfile.warmup_enabled || updateEmailProvider.isPending}
                          onClick={() =>
                            updateEmailProvider.mutate(
                              { id: epSelectedProfile.id, data: { warmup_enabled: false } },
                              {
                                onSuccess: () => {
                                  setEpSelectedProfile((p) => (p ? { ...p, warmup_enabled: false } : p));
                                  setEpSettingsForm((f) => ({ ...f, warmup_enabled: false }));
                                  toast.success("Warmup disabled.");
                                },
                              },
                            )
                          }
                        >
                          Disable
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!!epSelectedProfile.warmup_enabled || updateEmailProvider.isPending}
                          onClick={() =>
                            updateEmailProvider.mutate(
                              { id: epSelectedProfile.id, data: { warmup_enabled: true } },
                              {
                                onSuccess: () => {
                                  setEpSelectedProfile((p) => (p ? { ...p, warmup_enabled: true } : p));
                                  setEpSettingsForm((f) => ({ ...f, warmup_enabled: true }));
                                  toast.success("Warmup enabled.");
                                },
                              },
                            )
                          }
                        >
                          Enable
                        </button>
                      </div>
                    </div>
                    {epSelectedProfile && (
                      <div className="ep-warmup-today">
                        <p className="ep-warmup-summary-title">Today's send usage</p>
                        <div className="ep-warmup-today-row">
                          <span className="ep-warmup-today-count">
                            {epSelectedProfile.emails_sent_today ?? 0} / {epSelectedProfile.effective_daily_limit ?? epSelectedProfile.daily_campaign_limit} sent
                          </span>
                        </div>
                        <div className="ep-warmup-bar-track">
                          <div
                            className="ep-warmup-bar-fill"
                            style={{
                              width: `${Math.min(100, Math.round(((epSelectedProfile.emails_sent_today ?? 0) / Math.max(1, epSelectedProfile.effective_daily_limit ?? epSelectedProfile.daily_campaign_limit)) * 100))}%`,
                            }}
                          />
                        </div>
                        {epSelectedProfile.warmup_enabled && (
                          <p className="ep-warmup-bar-label">
                            Warmup cap today: {epSelectedProfile.effective_daily_limit ?? epSelectedProfile.daily_campaign_limit} / {epSelectedProfile.warmup_limit} target
                          </p>
                        )}
                      </div>
                    )}
                    <div className="ep-warmup-summary">
                      <p className="ep-warmup-summary-title">Summary for past week</p>
                      <div className="ep-warmup-stats">
                        <div className="ep-warmup-stat">
                          <span className="ep-warmup-stat-num">0</span>
                          <span className="ep-warmup-stat-label">warmup emails received</span>
                        </div>
                        <div className="ep-warmup-stat">
                          <span className="ep-warmup-stat-num">{epSendStatsData?.data?.total_sent ?? 0}</span>
                          <span className="ep-warmup-stat-label">warmup emails sent</span>
                        </div>
                        <div className="ep-warmup-stat">
                          <span className="ep-warmup-stat-num">0</span>
                          <span className="ep-warmup-stat-label">saved from spam</span>
                        </div>
                      </div>
                    </div>
                    <div className="ep-chart-placeholder">
                      <p className="ep-chart-label">Warmup Emails Sent</p>
                      {epSendStatsData?.data?.daily && epSendStatsData.data.daily.some((d) => d.sent > 0) ? (
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={epSendStatsData.data.daily} barCategoryGap="30%">
                            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "var(--gray-400)" }} />
                            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "var(--gray-400)" }} width={28} />
                            <Tooltip
                              cursor={{ fill: "var(--gray-50)" }}
                              contentStyle={{ border: "1px solid var(--gray-100)", borderRadius: 8, fontSize: 12 }}
                              formatter={(v: number) => [v, "Emails sent"]}
                              labelFormatter={(label) => label}
                            />
                            <Bar dataKey="sent" radius={[4, 4, 0, 0]}>
                              {epSendStatsData.data.daily.map((entry) => (
                                <Cell key={entry.date} fill={entry.sent > 0 ? "var(--green)" : "var(--gray-100)"} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="ep-chart-empty">No emails sent in the last 7 days.</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Settings Tab */}
                {epDetailTab === "settings" && (
                  <div className="ep-detail-body">
                    <div className="ep-settings-section">
                      <h4 className="ep-settings-section-title">Sender name</h4>
                      <div className="ep-settings-row">
                        <div className="form-group ep-settings-col">
                          <label className="label">First Name</label>
                          <input
                            type="text"
                            className="input-field"
                            value={epSettingsForm.first_name}
                            onChange={(e) => setEpSettingsForm((f) => ({ ...f, first_name: e.target.value }))}
                          />
                        </div>
                        <div className="form-group ep-settings-col">
                          <label className="label">Last Name</label>
                          <input
                            type="text"
                            className="input-field"
                            value={epSettingsForm.last_name}
                            onChange={(e) => setEpSettingsForm((f) => ({ ...f, last_name: e.target.value }))}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="ep-settings-section">
                      <h4 className="ep-settings-section-title">Signature</h4>
                      <SignatureEditor
                        value={epSettingsForm.signature}
                        onChange={(html) => setEpSettingsForm((f) => ({ ...f, signature: html }))}
                      />
                    </div>

                    <div className="ep-settings-section">
                      <h4 className="ep-settings-section-title">Campaign Settings</h4>
                      <div className="ep-settings-row">
                        <div className="form-group ep-settings-col">
                          <label className="label">Daily campaign limit</label>
                          <div className="ep-input-suffix-wrap">
                            <input
                              type="number"
                              className="input-field"
                              min={1}
                              value={epSettingsForm.daily_campaign_limit}
                              onChange={(e) =>
                                setEpSettingsForm((f) => ({ ...f, daily_campaign_limit: e.target.value === "" ? 0 : Number(e.target.value) }))
                              }
                            />
                            <span className="ep-input-suffix">emails</span>
                          </div>
                        </div>
                        <div className="form-group ep-settings-col">
                          <label className="label">Minimum wait time</label>
                          <div className="ep-input-suffix-wrap">
                            <input
                              type="number"
                              className="input-field"
                              min={0}
                              value={epSettingsForm.min_wait_time_minutes}
                              onChange={(e) =>
                                setEpSettingsForm((f) => ({ ...f, min_wait_time_minutes: e.target.value === "" ? 0 : Number(e.target.value) }))
                              }
                            />
                            <span className="ep-input-suffix">minute(s)</span>
                          </div>
                        </div>
                      </div>

                      <div className="form-group" style={{ marginTop: 16 }}>
                        <label className="label">Reply-to Address</label>
                        <input
                          type="email"
                          className="input-field"
                          value={epSettingsForm.reply_to_address}
                          onChange={(e) => setEpSettingsForm((f) => ({ ...f, reply_to_address: e.target.value }))}
                          placeholder="reply@example.com"
                        />
                      </div>

                    </div>

                    <div className="ep-settings-section">
                      <h4 className="ep-settings-section-title">Warmup Settings</h4>
                      <div className="ep-settings-toggle-row">
                        <div>
                          <p className="ep-settings-toggle-label">Warmup Enabled</p>
                          <p className="ep-settings-toggle-desc">
                            Gradually warm up this inbox to improve deliverability
                          </p>
                        </div>
                        <button
                          className="st-notif-toggle"
                          style={{ background: epSettingsForm.warmup_enabled ? "var(--sky)" : "var(--gray-200)" }}
                          onClick={() => setEpSettingsForm((f) => ({ ...f, warmup_enabled: !f.warmup_enabled }))}
                        >
                          <span
                            className="st-notif-thumb"
                            style={{ left: epSettingsForm.warmup_enabled ? "22px" : "2px" }}
                          />
                        </button>
                      </div>
                      <div className="ep-settings-row" style={{ marginTop: 12 }}>
                        <div className="form-group ep-settings-col">
                          <label className="label">Warmup Limit</label>
                          <div className="ep-input-suffix-wrap">
                            <input
                              type="number"
                              className="input-field"
                              min={1}
                              value={epSettingsForm.warmup_limit}
                              onChange={(e) =>
                                setEpSettingsForm((f) => ({ ...f, warmup_limit: e.target.value === "" ? 0 : Number(e.target.value) }))
                              }
                            />
                            <span className="ep-input-suffix">emails/day</span>
                          </div>
                        </div>
                        <div className="form-group ep-settings-col">
                          <label className="label">Warmup Increment</label>
                          <div className="ep-input-suffix-wrap">
                            <input
                              type="number"
                              className="input-field"
                              min={1}
                              value={epSettingsForm.warmup_increment}
                              onChange={(e) =>
                                setEpSettingsForm((f) => ({ ...f, warmup_increment: e.target.value === "" ? 0 : Number(e.target.value) }))
                              }
                            />
                            <span className="ep-input-suffix">per day</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ep-settings-section">
                      <h4 className="ep-settings-section-title">Custom Tracking Domain</h4>
                      <label className="ep-checkbox-row">
                        <input
                          type="checkbox"
                          className="st-sender-checkbox"
                          checked={epSettingsForm.custom_tracking_domain_enabled}
                          onChange={(e) =>
                            setEpSettingsForm((f) => ({ ...f, custom_tracking_domain_enabled: e.target.checked }))
                          }
                        />
                        <span>Enable custom tracking domain</span>
                      </label>
                      {epSettingsForm.custom_tracking_domain_enabled && (
                        <>
                          <div className="ep-field-row ep-field-row--spaced">
                            <label className="ep-field-label">Your domain</label>
                            <input
                              type="text"
                              className="ep-text-input"
                              placeholder="e.g. yourdomain.com"
                              value={epSettingsForm.custom_tracking_domain}
                              onChange={(e) =>
                                setEpSettingsForm((f) => ({ ...f, custom_tracking_domain: e.target.value }))
                              }
                            />
                            <span className="ep-field-hint">
                              Add the CNAME record below to your DNS, then enter that domain here.
                            </span>
                          </div>
                          <div className="ep-cname-box">
                            <div className="ep-cname-row">
                              <span className="ep-cname-key">Record Type</span>
                              <span>CNAME</span>
                            </div>
                            <div className="ep-cname-row">
                              <span className="ep-cname-key">Host</span>
                              <span>{epSettingsForm.custom_tracking_domain ? `inst.${epSettingsForm.custom_tracking_domain}` : "inst.yourdomain.com"}</span>
                            </div>
                            <div className="ep-cname-row">
                              <span className="ep-cname-key">Value</span>
                              <span>prox.itrackly.com</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <button
                      className="btn btn-primary"
                      disabled={updateEmailProvider.isPending}
                      onClick={() => {
                        const data = {
                          first_name: epSettingsForm.first_name || null,
                          last_name: epSettingsForm.last_name || null,
                          signature: epSettingsForm.signature || null,
                          reply_to_address: epSettingsForm.reply_to_address || null,
                          tags: epSettingsForm.tags,
                          daily_campaign_limit: epSettingsForm.daily_campaign_limit,
                          min_wait_time_minutes: epSettingsForm.min_wait_time_minutes,
                          campaign_slow_ramp: epSettingsForm.campaign_slow_ramp,
                          daily_inbox_placement_limit: epSettingsForm.daily_inbox_placement_limit,
                          custom_tracking_domain_enabled: epSettingsForm.custom_tracking_domain_enabled,
                          custom_tracking_domain: epSettingsForm.custom_tracking_domain_enabled
                            ? (epSettingsForm.custom_tracking_domain || null)
                            : null,
                          warmup_enabled: epSettingsForm.warmup_enabled,
                          warmup_limit: epSettingsForm.warmup_limit,
                          warmup_increment: epSettingsForm.warmup_increment,
                        };
                        updateEmailProvider.mutate(
                          { id: epSelectedProfile.id, data },
                          {
                            onSuccess: () => {
                              setEpSelectedProfile((p) => (p ? { ...p, ...data } : p));
                              toast.success("Settings saved.");
                            },
                            onError: (err: any) => toast.error(err?.message ?? "Failed to save."),
                          },
                        );
                      }}
                    >
                      {updateEmailProvider.isPending ? "Saving…" : "Save"}
                    </button>
                  </div>
                )}

                {/* Campaigns Tab */}
                {epDetailTab === "campaigns" && (
                  epCampaignsLoading ? (
                    <div className="ep-detail-body ep-campaigns-empty">
                      <p>Loading…</p>
                    </div>
                  ) : !epCampaignsData?.data?.length ? (
                    <div className="ep-detail-body ep-campaigns-empty">
                      <Mail size={36} color="var(--gray-200)" />
                      <p>No campaigns assigned to this account yet.</p>
                    </div>
                  ) : (
                    <div className="ep-detail-body ep-campaigns-list">
                      {epCampaignsData.data.map((campaign) => (
                        <div key={campaign.id} className="ep-campaign-row">
                          <div className="ep-campaign-info">
                            <span className="ep-campaign-name">{campaign.name}</span>
                            <span className={`ep-campaign-badge ep-campaign-badge--${campaign.status}`}>{campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}</span>
                          </div>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => navigate(`/app/campaigns/${campaign.id}`)}
                          >
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {activeTab === "Team" && (
            <div className="card st-team-card">
              <div className="st-team-header">
                <h2 className="st-team-title">Team Members</h2>
                {isOwner && (
                  <button className="btn btn-primary btn-sm" onClick={() => setShowInviteModal(true)}>
                    <Plus size={16} /> Invite Member
                  </button>
                )}
              </div>

              {teamLoading ? (
                <div className="st-team-loading">Loading team…</div>
              ) : (
                <>
                  <div>
                    <table className="st-table st-table-fixed">
                      <colgroup>
                        <col style={{ width: "50%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "18%" }} />
                        <col style={{ width: "14%" }} />
                      </colgroup>
                      <thead>
                        <tr className="st-thead-row">
                          <th className="st-team-th">Member</th>
                          <th className="st-team-th">Role</th>
                          <th className="st-team-th">Status</th>
                          <th className="st-team-th-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((m) => (
                          <tr key={m.id} className="st-tbody-row">
                            <td className="st-team-td">
                              <div className="st-team-member-cell">
                                <div className="st-team-avatar">{getAvatarInitials(m.name ?? m.email)}</div>
                                <div>
                                  <p className="st-team-member-name">{m.name}</p>
                                  <p className="st-team-member-email">{m.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="st-team-td">
                              <span className="badge badge-gray st-badge-capitalize">{m.role}</span>
                            </td>
                            <td className="st-team-td">
                              <span className="badge badge-green">Active</span>
                            </td>
                            <td className="st-team-td-right">
                              {isOwner && m.role !== "owner" && (
                                <button
                                  className="btn btn-ghost btn-sm st-btn-red"
                                  onClick={() => setConfirmRemove({ userId: m.id, name: m.name ?? m.email })}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {invitations.map((inv) => (
                          <tr key={inv.id} className="st-tbody-row">
                            <td className="st-team-td">
                              <div className="st-team-member-cell">
                                <div className="st-team-avatar-pending">{getAvatarInitials(inv.name ?? inv.email)}</div>
                                <div>
                                  <p className="st-team-member-name">{inv.name}</p>
                                  <p className="st-team-member-email">{inv.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="st-team-td">
                              <span className="badge badge-gray st-badge-capitalize">{inv.role}</span>
                            </td>
                            <td className="st-team-td">
                              <span className="badge badge-amber">Pending</span>
                            </td>
                            <td className="st-team-td-right">
                              {isOwner && (
                                <button
                                  className="btn btn-ghost btn-sm st-btn-red"
                                  onClick={() => setConfirmRevoke({ inviteId: inv.id, email: inv.email })}
                                >
                                  Revoke
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {members.length === 0 && invitations.length === 0 && (
                          <tr>
                            <td colSpan={4} className="st-team-empty-td">
                              No team members yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <InviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />

              {confirmRemove && (
                <ConfirmModal
                  title="Remove Member"
                  message={`Remove ${confirmRemove.name} from your organization? They will lose access immediately.`}
                  confirmLabel="Remove"
                  danger
                  onConfirm={async () => {
                    await removeMember.mutateAsync(confirmRemove.userId);
                    setConfirmRemove(null);
                  }}
                  onCancel={() => setConfirmRemove(null)}
                />
              )}

              {confirmRevoke && (
                <ConfirmModal
                  title="Revoke Invitation"
                  message={`Revoke the invitation sent to ${confirmRevoke.email}?`}
                  confirmLabel="Revoke"
                  danger
                  onConfirm={async () => {
                    await revokeInvite.mutateAsync(confirmRevoke.inviteId);
                    setConfirmRevoke(null);
                  }}
                  onCancel={() => setConfirmRevoke(null)}
                />
              )}
            </div>
          )}

          {activeTab === "Notifications" && (
            <div className="card">
              <h2 className="st-section-title">Notification Preferences</h2>
              {notifLoading ? (
                <p className="st-notif-loading">Loading preferences…</p>
              ) : (
                [
                  {
                    category: "Email Activity",
                    items: [
                      { label: "New replies", key: "new_replies" },
                      { label: "Bounced emails", key: "bounced_emails" },
                      { label: "Campaign completed", key: "campaign_completed" },
                    ],
                  },
                  {
                    category: "Lead Activity",
                    items: [{ label: "New leads added", key: "new_leads_added" }],
                  },
                  {
                    category: "System",
                    items: [
                      { label: "Weekly digest", key: "weekly_digest" },
                      { label: "Security alerts", key: "security_alerts" },
                    ],
                  },
                ].map((cat, ci, arr) => (
                  <div key={ci} className={ci < arr.length - 1 ? "st-notif-category" : "st-notif-category-last"}>
                    <h3 className="st-notif-cat-title">{cat.category}</h3>
                    <div className="st-notif-list">
                      {cat.items.map((item) => {
                        const enabled = notifPrefs[item.key] ?? false;
                        return (
                          <div key={item.key} className="st-notif-item">
                            <span className="st-notif-label">{item.label}</span>
                            <div
                              onClick={() => toggleNotif(item.key, enabled)}
                              className="st-notif-toggle"
                              style={{
                                background: enabled ? "var(--sky)" : "var(--gray-200)",
                                opacity: updateNotifMutation.isPending ? 0.6 : 1,
                              }}
                            >
                              <div className="st-notif-thumb" style={{ left: enabled ? 22 : 2 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {ci < arr.length - 1 && <div className="st-notif-divider" />}
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "Menu Permissions" && (
            <div>
              <h2 className="st-section-title">Menu Permissions</h2>
              <p className="st-menu-perms-desc">Control which sidebar menu items each team member can access.</p>

              {menuPermLoading || teamLoading ? (
                <div className="st-menu-perms-loading">Loading…</div>
              ) : members.filter((m) => m.role !== "owner").length === 0 ? (
                <div className="card st-menu-perms-empty">No members to configure. Invite team members first.</div>
              ) : (
                <div className="st-stack">
                  {members
                    .filter((m) => m.role !== "owner")
                    .map((m) => {
                      const perms = menuPermDraft[m.id] ?? new Set(MENU_ITEMS.map((i) => i.key));
                      const allChecked = visibleMenuItems.every((item) => perms.has(item.key));
                      return (
                        <div key={m.id} className="card st-mp-card">
                          <div className="st-mp-header">
                            <div className="st-mp-user-cell">
                              <div className="st-mp-user-avatar">{getAvatarInitials(m.name ?? m.email)}</div>
                              <div>
                                <p className="st-mp-user-name">{m.name}</p>
                                <p className="st-mp-user-meta">
                                  {m.email} · <span className="st-mp-user-role">{m.role}</span>
                                </p>
                              </div>
                            </div>
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={menuPermSaving[m.id]}
                              onClick={() => saveMenuPerms(m.id)}
                            >
                              {menuPermSaving[m.id] ? "Saving…" : "Save"}
                            </button>
                          </div>

                          <div className="st-mp-border-top">
                            <label className="st-mp-check-row st-mp-check-all">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={() => {
                                  setMenuPermDraft((prev) => ({
                                    ...prev,
                                    [m.id]: allChecked ? new Set() : new Set(visibleMenuItems.map((i) => i.key)),
                                  }));
                                }}
                              />
                              <span className="st-mp-all-menus-label">All Menus</span>
                            </label>
                            {(["Main Navigation", "Settings"] as const).map((group) => {
                              const groupItems = visibleMenuItems.filter((i) => i.group === group);
                              const allGroupChecked = groupItems.every((i) => perms.has(i.key));
                              return (
                                <div key={group} className="st-mp-group">
                                  <label className="st-mp-check-row st-mp-group-header">
                                    <input
                                      type="checkbox"
                                      checked={allGroupChecked}
                                      onChange={() => {
                                        setMenuPermDraft((prev) => {
                                          const set = new Set(prev[m.id] ?? MENU_ITEMS.map((i) => i.key));
                                          groupItems.forEach((i) =>
                                            allGroupChecked ? set.delete(i.key) : set.add(i.key),
                                          );
                                          return { ...prev, [m.id]: set };
                                        });
                                      }}
                                    />
                                    <span className="st-mp-group-label">{group}</span>
                                  </label>
                                  <div className="st-mp-grid">
                                    {groupItems.map((item) => (
                                      <label key={item.key} className="st-mp-check-row">
                                        <input
                                          type="checkbox"
                                          checked={perms.has(item.key)}
                                          onChange={() => toggleMenuPerm(m.id, item.key)}
                                        />
                                        <span className="st-mp-menu-item-label">{item.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bulk Delete Providers Confirm */}
      {confirmBulkDeleteProviders && (
        <ConfirmModal
          title="Delete Email Providers"
          message={`Are you sure you want to permanently delete ${epSelectedIds.size} email provider${epSelectedIds.size > 1 ? "s" : ""}?`}
          detail="This action cannot be undone."
          confirmLabel="Delete All"
          danger
          loading={deleteEmailProvider.isPending}
          onConfirm={async () => {
            const ids = Array.from(epSelectedIds);
            for (const id of ids) {
              await deleteEmailProvider.mutateAsync(id).catch(() => {});
            }
            toast.success(`${ids.length} provider${ids.length > 1 ? "s" : ""} deleted.`);
            setEpSelectedIds(new Set());
            setConfirmBulkDeleteProviders(false);
          }}
          onCancel={() => setConfirmBulkDeleteProviders(false)}
        />
      )}

      {/* Delete Provider Confirm — top-level so it works from any tab */}
      {confirmDeleteProvider && (
        <ConfirmModal
          title="Delete Email Provider"
          message="Are you sure you want to permanently delete this account?"
          detail={`${confirmDeleteProvider.label} · This action cannot be undone.`}
          confirmLabel="Delete"
          danger
          loading={deleteEmailProvider.isPending}
          onConfirm={() => {
            deleteEmailProvider.mutate(confirmDeleteProvider.id, {
              onSuccess: () => {
                toast.success("Email provider deleted.");
                setConfirmDeleteProvider(null);
              },
              onError: (err: any) => toast.error(err?.message ?? "Failed to delete."),
            });
          }}
          onCancel={() => setConfirmDeleteProvider(null)}
        />
      )}
    </div>
  );
}
